// ModelViewer.jsx
import React, {
    Suspense,
    useState,
    useRef,
    useEffect,
    useMemo,
    useCallback,
} from 'react';
import { Canvas } from '@react-three/fiber';
import { GizmoHelper, GizmoViewcube, GizmoViewport } from '@react-three/drei';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import Datagrid from "../../components/Datagrid/Datagrid"
import {
    Box,
    Button,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Tab,
    Tabs,
    Tooltip,
    Typography,
    Menu,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Badge,
    TextField,
    Checkbox,
    Card,
    CardContent,
    CardActionArea,
    alpha,
    Slider,
    RadioGroup,
    FormControlLabel,
    Radio,
    Switch,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
    useTheme,
} from '@mui/material';

import {
    Autorenew,
    CheckBox as CheckBoxIcon,
    Close,
    KeyboardDoubleArrowUp,
    Launch,
    LibraryAddCheck,
    Loop,
    NorthEast,
    QuestionMark,
    Search,
    ShoppingCart,
    Summarize,
    Menu as MenuIcon,
    Home as HomeIcon,
    OpenWith as OpenWithIcon,
    ZoomIn as ZoomInIcon,
    MoreVert as MoreVertIcon,
    Settings as SettingsIcon,
    ViewAgenda as ViewAgendaIcon,
    GridOn as GridOnIcon,
    TableChart as TableChartIcon,
    ExpandMore as ExpandMoreIcon,
    ChevronRight as ChevronRightIcon,
    CheckCircle,
    Block,
    Delete,
    OpenInFull,
    Info,
    InfoOutlined,
    SupportAgent,
    VerticalAlignTop,
} from '@mui/icons-material';

import LoadingBoxes from '../../components/LoadingBoxes/LoadingBoxes';
import { authHeader, isEmpty, toCapitalCase } from '../../utilities/globalServiceUtils';
import { getMachineByNumberOrId } from '../../services/myMachineService';
import { useSite } from '../../context/siteContext';
import { useAuth } from '../../context/authContext';
import {
    getProductsByMachineId,
    normalizeMachineProductsResponse,
} from '../../services/machineCardService';
import { addModelCartItems } from '../../services/cartService';
import { ASSETS_BASE_URL, DEFAULT_AXIS_MODEL_VIEWER, RSPL_LIST, SERVER_BASE_URL } from '../../config';
import { useOptions } from "../../context/optionContext";
import { themeColors } from "../../utilities/themeColors";
import PageTitle from '../../components/PageTitle/PageTitle';
import ModelScene from '../../components/ModelScene/ModelScene';
import ModalWrapper from '../../components/ModalWrapper/ModalWrapper';
import ContactCCRForm from '../../components/ContactCCRForm/ContactCCRForm';
import ReplacementPartNotice from '../../components/ReplacementPartNotice/ReplacementPartNotice';
import { getEffectiveProductDisplay, getEffectiveProductTarget } from '../../utilities/replacementProduct';
import Swal from "sweetalert2";
import { FiChevronsDown, FiChevronsUp } from "react-icons/fi";
import { BsTransparency } from "react-icons/bs";
import { PiDropSimpleFill } from "react-icons/pi";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import { trackEvent, trackSearchPerformed } from "../../utilities/analyticsService";
// ---------------------- helpers / constants ----------------------

const loadingMessages = [
    'Loading 3D Model...',
    'Loading Products and Machine Data...',
    'Rendering Model View...',
    'Model Loaded',
];

const MODEL_AXIS_CHOICES = ['X', 'Y', 'Z'];

const normalizeAxisKey = (value, fallback = 'Y') => {
    const raw = String(value || '')
        .trim()
        .toUpperCase()
        .replace(/[^A-Z]/g, '');
    if (raw === 'X' || raw === 'X_UP') return 'X';
    if (raw === 'Y' || raw === 'Y_UP') return 'Y';
    if (raw === 'Z' || raw === 'Z_UP') return 'Z';
    return fallback;
};

const resolveModelOrientation = (optionKeys) => {
    const fallbackUp = normalizeAxisKey(DEFAULT_AXIS_MODEL_VIEWER || 'Y_UP', 'Y');
    const up = normalizeAxisKey(
        optionKeys?.modelViewerOrientationUp || optionKeys?.modelViewerUpAxis,
        fallbackUp
    );
    const left = normalizeAxisKey(
        optionKeys?.modelViewerOrientationLeft,
        up === 'X' ? 'Z' : 'X'
    );
    const right = normalizeAxisKey(optionKeys?.modelViewerOrientationRight, 'X');
    const isValid =
        MODEL_AXIS_CHOICES.includes(up) &&
        MODEL_AXIS_CHOICES.includes(left) &&
        MODEL_AXIS_CHOICES.includes(right) &&
        new Set([up, left, right]).size === 3;

    if (isValid) return { up, left, right };

    const fallbackLeft = up === 'X' ? 'Z' : 'X';
    const fallbackRight = MODEL_AXIS_CHOICES.find(
        (axis) => axis !== up && axis !== fallbackLeft
    ) || 'Y';
    return { up, left: fallbackLeft, right: fallbackRight };
};

const CAMERA_CORNER_CHOICES = new Set([
    'isometric',
    'front',
    'back',
    'left',
    'right',
    'top',
    'bottom',
]);

const normalizeCameraCorner = (value) => {
    const raw = String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[_\s]+/g, '-')
        .replace(/[^a-z-]/g, '');
    return CAMERA_CORNER_CHOICES.has(raw) ? raw : 'isometric';
};


const resolveProductCardImageUrl = (product = {}) => {
    const raw = product?.asset_data?.images?.[0];
    if (raw) {
        const normalized = String(raw).trim();
        if (
            /^(https?:)?\/\//i.test(normalized) ||
            normalized.startsWith('data:')
        ) {
            return normalized;
        }
        const path = normalized.startsWith('/') ? normalized : `/${normalized}`;
        return `${ASSETS_BASE_URL}${path}`;
    }
    return `${ASSETS_BASE_URL}/ProductImages/TMB/${product?.product_number}.JPG`;
};

const resolvePartCardImageUrl = (data = {}, stockImageUrl = null) => {
    const explicit = data?.productImageURL;
    if (explicit) {
        const normalized = String(explicit).trim();
        if (
            /^(https?:)?\/\//i.test(normalized) ||
            normalized.startsWith('data:')
        ) {
            return normalized;
        }
        if (normalized.startsWith('/')) {
            return `${ASSETS_BASE_URL}${normalized}`;
        }
        return normalized;
    }

    const raw = data?.asset_data?.images?.[0];
    if (raw) {
        const normalized = String(raw).trim();
        if (
            /^(https?:)?\/\//i.test(normalized) ||
            normalized.startsWith('data:')
        ) {
            return normalized;
        }
        const path = normalized.startsWith('/') ? normalized : `/${normalized}`;
        return `${ASSETS_BASE_URL}${path}`;
    }

    const fallbackNumber =
        data?.part_number || data?.product_number || data?.partNumber;
    if (fallbackNumber) {
        return `${ASSETS_BASE_URL}/ProductImages/TMB/${fallbackNumber}.JPG`;
    }

    return stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
};

export const TabPanel = ({
    children,
    value,
    index,
    title,
    subtitle,
    headerActions = null,
    ...other
}) => {
    return (
        <Box
            {...other}
            sx={{
                height: '100%',
                px: 2,
                pb: 2,
                pt: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden', // keep outer non-scrollable
                flex: 1,
                minHeight: 0, // IMPORTANT: allow inner flex child to shrink
            }}
        >
            {value === index && (
                <>
                    {(title || headerActions) && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                mb: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minWidth: 0,
                                }}
                            >
                                {title ? (
                                    <Typography fontWeight="bold" variant="body1" mb={0}>
                                        {title}
                                    </Typography>
                                ) : null}
                                {subtitle ? (
                                    <Typography variant="body2" fontSize="11px" mb={0}>
                                        {subtitle}
                                    </Typography>
                                ) : null}
                            </Box>
                            {headerActions ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        '& > *': {
                                            display: 'flex',
                                            alignItems: 'center',
                                        },
                                    }}
                                >
                                    {headerActions}
                                </Box>
                            ) : null}
                        </Box>
                    )}
                    {children}
                </>
            )}
        </Box>
    );
}

// Fixed SearchInput: split change vs submit
export const SearchInput = ({
    value,
    onChange,
    onSubmit,
    placeholder = 'Search...',
}) => {
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && onSubmit) {
            onSubmit();
        }
    };

    return (
        <Box>
            <TextField
                variant="outlined"
                placeholder={placeholder}
                fullWidth
                value={value}
                onChange={onChange}
                size="small"
                slotProps={{
                    input: {
                        endAdornment: (
                            <Box display="flex">
                                <IconButton
                                    size="small"
                                    sx={{ mr: '-2px' }}
                                    onClick={onSubmit}
                                >
                                    <Search fontSize="small" />
                                </IconButton>
                            </Box>
                        ),
                    },
                }}
                sx={{
                    '& .MuiInputBase-root': {
                        paddingRight: '2px',
                    },
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent',
                    },
                    '& .MuiInputBase-input::placeholder': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    },
                    width: '100%',
                }}
                onKeyDown={handleKeyDown}
            />
        </Box>
    );
};

export const PartMenuButton = ({
    icon = null,
    buttonText = '',
    isActive = false,
    onClick = null,
    price = 0,
}) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
            }}
            onClick={onClick ?? undefined}
        >
            <Box sx={{ minHeight: '32px' }}>
                {icon && (
                    <IconButton
                        size="small"
                        color="inherit"
                        sx={{
                            color: isActive
                                ? (theme) => theme.palette.primary.main
                                : 'inherit',
                        }}
                    >
                        {icon}
                    </IconButton>
                )}
                {price ? (
                    <Typography
                        textWrap="nowrap"
                        variant="body1"
                        fontSize="14px"
                        mb={0}
                        fontWeight="bold"
                    >
                        {price}
                    </Typography>
                ) : null}
            </Box>
            <Typography
                textWrap="nowrap"
                variant="body2"
                fontSize="10px"
                sx={{
                    color: isActive
                        ? (theme) => theme.palette.primary.main
                        : 'inherit',
                }}
            >
                {buttonText}
            </Typography>
        </Box>
    );
};

export const PartMenu = ({
    onHeaderMouseDown,
    data,
    onClose,
    onShowParentClick,
    onZoomToPart,
    onIsolateClick,
    occurrenceToggle,
    onOccurrenceToggle,
    isInCart,
    isInRequest,
    addToCart,
    addToRequestList,
    enableModelActions = true,
    showPrice = true,
}) => {
    const { t } = useTranslation();
    const canUse3DViewActions = data?.availableIn3D !== false;
    const actionPartNumber = data?.effective_part_number || data?.part_number;
    const actionPrice = data?.effective_price ?? data?.price;
    const actionPurchasable = data?.effective_purchasable ?? data?.purchasable;
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '60px',
                width: '100%',
                minWidth: '275px',
                maxWidth: '550px',
                borderRadius: '10px',
                boxShadow: '0 0 6px 1px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                background: (theme) => theme.palette.background.paper,
                p: 1.25,
                userSelect: 'none',
            }}
        >
            {/* HEADER = drag handle */}
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'grab',
                }}
                onMouseDown={onHeaderMouseDown}
            >
                <Box sx={{ width: '100%', flex: 1 }}>
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            columnGap: 1,
                        }}
                    >
                        <Typography
                            fontWeight="bold"
                            variant="body1"
                            mb={0}
                            sx={{
                                maxWidth: "260px",
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                            }}
                        >
                            {data?.part_name || "Selected Part"}
                        </Typography>

                        {data?.url && !isEmpty(data?.url) && (
                            <Launch
                                sx={{ fontSize: '13px', cursor: 'pointer' }}
                                onClick={() => {
                                    window.open(data?.url, "_blank", "noopener,noreferrer");
                                }}
                            />
                        )}
                    </Box>
                    {data?.isOrderable ? (
                        <>
                            <Typography
                                variant="body2"
                                fontSize="11px"
                                mb={1}
                                color="text.secondary"
                                noWrap
                                sx={{ maxWidth: "260px", textOverflow: "ellipsis", overflow: "hidden" }}
                            >
                                {t('UI_TEXT.PART')}: {data.part_number} | Part Type: {data.part_type}
                            </Typography>
                            <ReplacementPartNotice product={data} sx={{ mb: 0.5 }} />
                        </>
                    ) : (
                        <Typography
                            variant="body2"
                            fontSize="11px"
                            mb={1}
                            color="text.secondary"
                        >
                            Non-Orderable Part
                        </Typography>
                    )}

                </Box>
                <Tooltip title={t("UI_TEXT.TOOLTIP_CLOSE")}>
                    <IconButton size="small" onClick={onClose}>
                        <Close />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* CONTENT */}
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2,
                    columnGap: 2,
                }}
            >
                {/* ORDERABLE UI */}
                {data?.isOrderable ? (
                    <>
                        {actionPrice && actionPurchasable && addToCart ? (
                            <>

                                {showPrice && <PartMenuButton price={actionPrice} buttonText="Price" />}
                                <PartMenuButton
                                    icon={<ShoppingCart />}
                                    buttonText={t('BUTTONS.ADD_TO_CART')}
                                    isActive={isInCart(actionPartNumber)}
                                    onClick={() => {
                                        if (isInCart(actionPartNumber)) return;
                                        const occurrenceQty =
                                            occurrenceToggle && data?.occurrenceCount
                                                ? data.occurrenceCount
                                                : 1;
                                        addToCart({
                                            ...data,
                                            product_id: data.effective_product_id || data.product_id,
                                            part_number: actionPartNumber,
                                            price: actionPrice,
                                            purchasable: actionPurchasable,
                                            qty: occurrenceQty,
                                        });
                                    }}
                                />
                            </>
                        ) : (addToRequestList ? <>

                            <PartMenuButton
                                icon={<Summarize />}
                                buttonText="Add to Request"
                                isActive={isInRequest(actionPartNumber)}
                                onClick={() => !isInRequest(actionPartNumber) && addToRequestList({
                                    ...data,
                                    product_id: data.effective_product_id || data.product_id,
                                    part_number: actionPartNumber,
                                    price: actionPrice,
                                    purchasable: actionPurchasable,
                                })}
                            />
                        </> : null)}


                    </>
                ) : (
                    /* NON-ORDERABLE → SHOW NOTHING HERE */
                    <></>
                )}


                {enableModelActions && (
                    <>
                        {canUse3DViewActions && onShowParentClick && (
                            <PartMenuButton
                                icon={<KeyboardDoubleArrowUp />}
                                buttonText="Show Parent"
                                onClick={onShowParentClick}
                            />
                        )}
                        {canUse3DViewActions && onZoomToPart && (
                            <PartMenuButton
                                icon={<ZoomInIcon />}
                                buttonText="Zoom to Part"
                                onClick={onZoomToPart}
                            />
                        )}
                        {canUse3DViewActions && onIsolateClick && (
                            <PartMenuButton
                                icon={<NorthEast />}
                                buttonText="Isolate"
                                onClick={onIsolateClick}
                            />
                        )}
                        {data?.hasMultipleOccurrences && (
                            <PartMenuButton
                                icon={
                                    occurrenceToggle ? (
                                        <CheckBoxIcon />
                                    ) : (
                                        <LibraryAddCheck />
                                    )
                                }
                                buttonText={
                                    occurrenceToggle
                                        ? "Select Current"
                                        : "Select All Occurrences"
                                }
                                onClick={onOccurrenceToggle}
                                isActive={occurrenceToggle}
                            />
                        )}
                    </>
                )}

            </Box>
        </Box>
    );
};

// Multiple parts menu – appears after "Select Visible"
export const MultiplePartsMenu = ({
    count,
    items = [],
    onClose,
    onAddToCart,
    findMatchingProductForPart,
    getSitePrice,
    machineId,
    onHeaderMouseDown,
    showPrice = true,
}) => {
    const { t } = useTranslation();
    const normalized = (value) => String(value ?? "").trim().toLowerCase();

    const uniqueItems = [];
    const seenPartNumbers = new Set();

    items.forEach((part, idx) => {
        const label =
            part?.assemblyNode?.name ||
            part?.name ||
            `Part ${idx + 1}`;

        const matchedProduct = findMatchingProductForPart(
            label,
            part?.assemblyNode?.productRef
        );

        const displayName =
            matchedProduct?.product_name ||
            part?.part_name ||
            label;
        const displayNumber =
            matchedProduct?.product_number ||
            part?.part_number ||
            part?.product_number ||
            "N/A";
        const dedupeKey = normalized(displayNumber);
        if (seenPartNumbers.has(dedupeKey)) return;
        seenPartNumbers.add(dedupeKey);

        const formattedPrice = matchedProduct
            ? getSitePrice(matchedProduct)
            : null;

        uniqueItems.push({
            key: `${dedupeKey}-${idx}`,
            part,
            cardData: {
                part_name: displayName,
                part_number: displayNumber,
                productImageURL: matchedProduct?.productImageURL ?? null,
                url:
                    matchedProduct?.product_id
                        ? `/machines/${machineId}/${matchedProduct.product_id}`
                        : "",
                purchasable: matchedProduct?.purchasable ?? false,
                price: formattedPrice,
                availableIn3D: true,
                alternative_item_number: matchedProduct?.alternative_item_number,
                replacement: matchedProduct?.replacement,
            },
        });
    });

    const uniqueCount = uniqueItems.length || count || 0;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '120px',
                maxHeight: '450px',
                width: '100%',
                minWidth: '275px',
                maxWidth: '480px',
                borderRadius: '10px',
                boxShadow: '0 0 6px 1px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                background: (theme) => theme.palette.background.paper,
                p: 1.5,
                userSelect: 'none',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                    cursor: 'grab',
                }}
                onMouseDown={onHeaderMouseDown}
            >
                <Box>
                    <Typography fontWeight="bold" variant="body1" mb={0}>
                        Multiple Parts Selected
                    </Typography>
                    <Typography
                        variant="body2"
                        fontSize="11px"
                        color="text.secondary"
                    >
                        {uniqueCount} unique parts selected
                    </Typography>
                </Box>
                <Tooltip title={t("UI_TEXT.TOOLTIP_CLOSE")}>
                    <IconButton size="small" onClick={onClose}>
                        <Close />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* LIST AREA */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    maxHeight: "400px",   // UPDATED
                    pr: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >

                {uniqueItems.length === 0 ? (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: "center", mt: 2 }}
                    >
                        No parts found.
                    </Typography>
                ) : (
                    uniqueItems.map((item) => {
                        return (
                            <PartCard
                                key={item.key}
                                data={item.cardData}
                                isActive={false}
                                showPrice={showPrice}
                            />
                        );
                    })

                )}
            </Box>

            {/* ACTIONS */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    columnGap: 1,
                    mt: 2,
                }}
            >
                <Button variant="text" onClick={onClose}>
                    {t("BUTTON.CLOSE")}
                </Button>
                {onAddToCart && (
                    <Button variant="contained" onClick={onAddToCart} startIcon={<ShoppingCart />}>
                        Add All to Cart
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export const PartCard = ({ data, isActive, warning, showWarning = false, showPrice = true }) => {
    const { t } = useTranslation();
    const { stockImageUrl } = useBrandingAssets();
    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '450px',
                display: 'flex',
                alignItems: 'center',
                py: 1,
                px: 1,
                columnGap: 1.5,



                borderRadius: "8px",
                background: isActive
                    ? (theme) => alpha(theme.palette.primary.main, 0.2)
                    : warning
                        ? (theme) => alpha(theme.palette.warning.main, 0.2)
                        : "transparent",

                "&:hover": {
                    background: (theme) => alpha(theme.palette.action.hover, 0.1)
                }
            }}
        >
            <Box
                sx={{
                    height: '45px',
                    width: '45px',
                    borderRadius: '8px',
                    boxShadow: '0 0 6px 1px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                }}
            >
                <img
                    src={resolvePartCardImageUrl(data, stockImageUrl)}
                    alt={data?.part_name || data?.part_number || "Part image"}
                    onError={(e) => {
                        e.currentTarget.src = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
                    }}
                    height="100%"
                    width="100%"
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",      // important
                        display: "block"         // removes inline gaps
                    }}
                />

            </Box>

            <Box
                sx={{
                    flex: 1,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: 0,
                }}
            >
                {/* PART NAME */}
                <Tooltip title={data?.part_name || ""} placement="top">
                    <Box sx={{ display: "flex", alignItems: "center", columnGap: 0.5, maxWidth: "240px" }}>
                        <Typography
                            variant="body1"
                            fontWeight="bold"
                            noWrap
                            sx={{
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                maxWidth: "220px",
                                cursor: "default"
                            }}
                        >
                            {data?.part_name}
                        </Typography>
                        {data?.url && !isEmpty(data?.url) && (
                            <Launch
                                sx={{ fontSize: '13px', cursor: 'pointer', flexShrink: 0 }}
                                onClick={() => {
                                    window.open(data?.url, "_blank", "noopener,noreferrer");
                                }}
                            />
                        )}
                    </Box>
                </Tooltip>



                {/* PART NUMBER */}
                <Tooltip title={data?.part_number || ""} placement="top">
                    <Typography
                        variant="body1"
                        fontSize="11px"
                        color="text.secondary"
                        noWrap
                        sx={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            maxWidth: "240px",
                            cursor: "default"
                        }}
                    >
                        {t('UI_TEXT.PART')}: {data?.part_number}
                    </Typography>
                </Tooltip>
                <ReplacementPartNotice product={data} />

                {showWarning && <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 0.25 }}>

                    <Typography
                        variant="body1"
                        fontSize={"10px"}
                        color='text.secondary'
                        noWrap
                        sx={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            maxWidth: "240px", // adjust if needed
                            cursor: "default"
                        }}
                    >
                        {data?.availableIn3D ? (
                            t('GENERAL.AVAILABLE_IN_3D')
                        ) : (
                            t('GENERAL.NOT_AVAILABLE_IN_3D')
                        )}
                    </Typography>
                </Box>}

            </Box>

            {showPrice && data?.purchasable && data?.price ? (
                <Typography variant="body1" color="primary" fontWeight="bold" noWrap>
                    {data.price}
                </Typography>
            ) : null}
        </Box>
    );
};

const MemoPartCard = React.memo(PartCard);


// ---------- Assembly helpers ----------

function buildAssemblyIndex(root) {
    if (!root) return null;

    const nodeById = new Map();
    const parentById = new Map();
    const objectToNodeId = new Map(); // object uuid -> one nodeId (first match)
    const productRefToNodeIds = new Map(); // productRef -> [nodeId...]
    let counter = 0;

    const traverse = (node, parentId = null) => {
        const id = `n-${counter++}`;
        node._id = id;
        nodeById.set(id, node);

        if (parentId !== null) {
            parentById.set(id, parentId);
        }

        if (node.productRef !== null && node.productRef !== undefined) {
            const key = String(node.productRef);
            const keys = new Set([key, key.trim().replace(/^#+/, '')].filter(Boolean));
            keys.forEach((refKey) => {
                if (!productRefToNodeIds.has(refKey)) {
                    productRefToNodeIds.set(refKey, []);
                }
                productRefToNodeIds.get(refKey).push(id);
            });
        }

        if (Array.isArray(node.objects)) {
            node.objects.forEach((obj) => {
                if (obj && obj.uuid && !objectToNodeId.has(obj.uuid)) {
                    objectToNodeId.set(obj.uuid, id);
                }
            });
        }

        if (Array.isArray(node.children)) {
            node.children.forEach((child) => traverse(child, id));
        }
    };

    traverse(root, null);
    const rootId = root._id;

    return { nodeById, parentById, rootId, objectToNodeId, productRefToNodeIds };
}

function getNodeQuantity(node) {
    if (!node) return null;
    return (
        node.quantity ??
        node.qty ??
        node.Qty ??
        node.childCount ??
        null
    );
}

function renderAssemblyWithQuantities(node, level = 0) {
    if (!node) return null;
    const qty = getNodeQuantity(node);

    return (
        <Box key={node._id || `${node.name}-${level}-${Math.random()}`}>
            <Typography
                variant="body2"
                sx={{
                    fontSize: 12,
                    pl: level * 2,
                    mb: 0.25,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                }}
            >
                {node.name || '(no name)'}
                {qty != null && `  x${qty}`}
            </Typography>
            {Array.isArray(node.children) &&
                node.children.map((child) =>
                    renderAssemblyWithQuantities(child, level + 1)
                )}
        </Box>
    );
}

const HEX_COLOR_REGEX = /^#?[0-9a-fA-F]{6}$/;
const normalizeHexColor = (value) => {
    const trimmed = String(value || "").trim();
    if (!HEX_COLOR_REGEX.test(trimmed)) return null;
    return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
};

// ------------------------- main component -------------------------

const ModelViewer = () => {
    const { machine_id, fileName } = useParams();
    const { selectedSiteData, selectedSite, selectedSiteCurrency, getSitePrice } = useSite();
    const { user, canViewProductPrice, canAddToCart, canAddToRequestList } = useAuth();
    const theme = useTheme();
    const { t } = useTranslation();
    const canShowAddToCart = canAddToCart();
    const canShowAddToRequestList = canAddToRequestList();
    const canShowPrice = canViewProductPrice();

    const navigate = useNavigate()
    const location = useLocation();
    const analyticsTracked3dOpen = Boolean(location?.state?.analyticsTracked3dOpen);
    const returnPath = useMemo(() => {
        const fromPath = location?.state?.fromPath;
        if (typeof fromPath === "string" && fromPath.trim()) return fromPath;
        return "/machines";
    }, [location?.state?.fromPath]);
    // machine + products
    const [machine, setMachine] = useState(null);
    const [error, setError] = useState(null);
    const [products, setProducts] = useState([]);
    const [machineName, setMachineName] = useState('');
    const [machineNumber, setMachineNumber] = useState('');
    const [bomTreeData, setBomTreeData] = useState([]);

    // left panel / tabs
    const [showMenuBox, setShowMenuBox] = useState(true);
    const [tabValue, setTabValue] = useState(0);

    // dialogs
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [bomOpen, setBomOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [ccrOpen, setCcrOpen] = useState(false);
    const [ccrPrefill] = useState(null);

    // part menu
    const [showPartMenu, setShowPartMenu] = useState(false);
    const [partMenuData, setPartMenuData] = useState(null);

    // multiple parts menu
    const [showMultiPartMenu, setShowMultiPartMenu] = useState(false);
    const [multiPartSelection, setMultiPartSelection] = useState([]);

    // loading / progress
    const [loading, setLoading] = useState(true);
    const [modelLoaded, setModelLoaded] = useState(false);
    const [dataReady, setDataReady] = useState(false);
    const [percentage, setPercentage] = useState(0);
    const [loadingStep, setLoadingStep] = useState(0);
    const [cartCount] = useState(0);
    const [cartOpen, setCartOpen] = useState(false);

    // part menu drag
    const [partMenuDelta, setPartMenuDelta] = useState({ x: 0, y: 0 });
    const [isDraggingPartMenu, setIsDraggingPartMenu] = useState(false);
    const [multiPartMenuDelta, setMultiPartMenuDelta] = useState({ x: 0, y: 0 });
    const [isDraggingMultiPartMenu, setIsDraggingMultiPartMenu] = useState(false);
    const dragStartRef = useRef({ mouseX: 0, mouseY: 0, dx: 0, dy: 0 });
    const multiDragStartRef = useRef({ mouseX: 0, mouseY: 0, dx: 0, dy: 0 });

    // three scene
    const sceneRef = useRef(null);
    const lastFetchKeyRef = useRef(null);

    // assembly / tree
    const [selectedPart, setSelectedPart] = useState(null);
    const [selectedParts, setSelectedParts] = useState([]); // all current logical selections
    const [assemblyTree, setAssemblyTree] = useState(null);
    const assemblyIndex = useMemo(
        () => (assemblyTree ? buildAssemblyIndex(assemblyTree) : null),
        [assemblyTree]
    );

    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [expanded, setExpanded] = useState([]);
    const [treeSearchTerm, setTreeSearchTerm] = useState('');
    const [treeSearchMatches, setTreeSearchMatches] = useState([]);
    const [treeSearchIndex, setTreeSearchIndex] = useState(0);

    const [nodeVisibility, setNodeVisibility] = useState({});
    const treeItemRefs = useRef(new Map());
    const pendingTreeSearchScrollRef = useRef(null);
    const partsTreeScrollRef = useRef(null);
    const subAssembliesScrollRef = useRef(null);
    const rsplScrollRef = useRef(null);

    const [subSearchTerm, setSubSearchTerm] = useState('');
    const [selectedSubAssemblyIds, setSelectedSubAssemblyIds] = useState([]);

    // nav mode for orbit controls
    const [navMode, setNavMode] = useState('rotate'); // 'rotate' | 'pan'

    // explode state for slider
    const [explodeEnabled, setExplodeEnabled] = useState(false);
    const [explodeValue, setExplodeValue] = useState(0.5); // 0..1

    const [occurrenceToggle, setOccurrenceToggle] = useState(false);
    const [machineTransparentToggle, setMachineTransparentToggle] = useState(false);
    const [partTransparencyToggle, setPartTransparencyToggle] = useState(false);
    const [partTransparencyOverrides, setPartTransparencyOverrides] = useState({});
    const [isolateSelectionToggle, setIsolateSelectionToggle] = useState(false);
    const [selectVisibleActive, setSelectVisibleActive] = useState(false);
    const [modelVisible, setModelVisible] = useState(true);
    const [non3DMessageVisible, setNon3DMessageVisible] = useState(false);
    const [non3DPartNumber, setNon3DPartNumber] = useState("");

    const [progressPhase, setProgressPhase] = useState("loading"); // "loading" | "finishing"
    // Selected inner tab (product type)

    const [rsplSearchTerm, setRsplSearchTerm] = useState("");
    const [rsplSearchResults, setRsplSearchResults] = useState([]);
    const [rsplSearchIndex, setRsplSearchIndex] = useState(0);
    const lastTrackedSearchRef = useRef("");
    const [activeRsplPartNumber, setActiveRsplPartNumber] = useState(null);

    const [rsplReady, setRsplReady] = useState(false);
    const [rsplLoading, setRsplLoading] = useState(false);
    const [suspendRsplFilter, setSuspendRsplFilter] = useState(false);
    const lastModelSelectionAtRef = useRef(0);
    const keepPartMenuOnNextClearRef = useRef(false);
    const skipMatrixApplyOnNextDeselectRef = useRef(false);
    const hiddenPartsFiloStackRef = useRef([]);
    const { optionKeys } = useOptions();
    const modelOrientation = useMemo(() => resolveModelOrientation(optionKeys || {}), [optionKeys]);
    const machineViewMode = useMemo(() => {
        if (isolateSelectionToggle && machineTransparentToggle) {
            return "isolatedMachineTransparent";
        }
        if (isolateSelectionToggle) {
            return "isolated";
        }
        if (machineTransparentToggle) {
            return "machineTransparent";
        }
        return "normal";
    }, [isolateSelectionToggle, machineTransparentToggle]);
    const defaultUpAxis = `${modelOrientation.up}_UP`;
    const defaultCameraCorner = useMemo(
        () => normalizeCameraCorner(optionKeys?.modelViewerDefaultCameraCorner),
        [optionKeys?.modelViewerDefaultCameraCorner]
    );
    const defaultLightIntensity = useMemo(() => {
        const parsed = Number.parseFloat(optionKeys?.modelViewerLightIntensity);
        return Number.isFinite(parsed) ? parsed : 1;
    }, [optionKeys?.modelViewerLightIntensity]);
    const defaultHighlightColor = useMemo(() => {
        const raw = optionKeys?.modelViewerHighlightColor;
        if (themeColors[raw]) return themeColors[raw].main;
        const fromOptions = normalizeHexColor(raw);
        const fromTheme = normalizeHexColor(theme?.palette?.primary?.light);
        return fromOptions || fromTheme || "#ffcc00";
    }, [optionKeys?.modelViewerHighlightColor, theme?.palette?.primary?.light]);
    const defaultHighlightOpacity = useMemo(() => {
        const parsed = Number.parseFloat(optionKeys?.modelViewerHighlightOpacity);
        return Number.isFinite(parsed) ? Math.min(1, Math.max(0, parsed)) : 1;
    }, [optionKeys?.modelViewerHighlightOpacity]);
    const showMenuTabSubtitles = optionKeys?.modelViewerShowTabSubtitles !== "false";
    const menuTabLabels = useMemo(() => ({
        subAssemblies: optionKeys?.modelViewerTabTitleSubAssemblies || "Sub Assemblies",
        rspl: optionKeys?.modelViewerTabTitleRspl || "RSPL Parts",
        partsTree: optionKeys?.modelViewerTabTitlePartsTree || "BOM",
    }), [
        optionKeys?.modelViewerTabTitleSubAssemblies,
        optionKeys?.modelViewerTabTitleRspl,
        optionKeys?.modelViewerTabTitlePartsTree,
    ]);
    const gizmoAxisLabels = useMemo(() => {
        if (modelOrientation?.up && modelOrientation?.left && modelOrientation?.right) {
            // GizmoViewport labels map to +X, +Y, +Z.
            // Keep them aligned with chosen Right/Up/Left mapping.
            return [modelOrientation.right, modelOrientation.up, modelOrientation.left];
        }
        return ["X", "Y", "Z"];
    }, [modelOrientation]);


    const [bomData, setBomData] = useState([]);
    const [settings, setSettings] = useState({
        navMode: "rotate",
        transparentOnSelect: false,
        explodeEnabled: false,
        explodeValue: 0.5,
        autoZoom: false,
        showGizmo: true,
        light: defaultLightIntensity,
    });

    const handleHelpClick = () => {
        setHelpOpen(true);
    };

    useEffect(() => {
        setNavMode(settings.navMode);
    }, [settings.navMode]);

    useEffect(() => {
        setExplodeEnabled(settings.explodeEnabled);
    }, [settings.explodeEnabled]);

    useEffect(() => {
        setExplodeValue(settings.explodeValue);
    }, [settings.explodeValue]);

    useEffect(() => {
        setSettings((prev) => ({ ...prev, light: defaultLightIntensity }));
    }, [defaultLightIntensity]);

    // ---- CART + REQUEST LIST STATE ----
    const [cartItems, setCartItems] = useState([]);
    const [requestItems, setRequestItems] = useState([]);
    const [orderingItems, setOrderingItems] = useState(false);

    const getModelAnalyticsPayload = useCallback(
        (extra = {}) => ({
            user_id: user?.user_id || "",
            user_email: user?.user_email || "",
            user_name:
                user?.user_display_name ||
                `${user?.user_first_name || ""} ${user?.user_last_name || ""}`.trim(),

            customer_id: selectedSiteData?.cus_id || "",
            customer_name:
                selectedSiteData?.parent_customer_name ||
                selectedSiteData?.customer_name ||
                "",
            customer_number: selectedSiteData?.customer_number || "",

            site_id: selectedSiteData?.site_id || "",
            site_name:
                selectedSiteData?.customer_site_name ||
                selectedSiteData?.site_name ||
                selectedSite ||
                "",
            site_number:
                selectedSiteData?.customer_site_number ||
                selectedSite ||
                "",
            customer_site_number:
                selectedSiteData?.customer_site_number ||
                selectedSite ||
                "",

            legal_entity:
                selectedSiteData?.site_legal_entity ||
                selectedSiteData?.legal_entity ||
                "",

            machine_id: machine_id || "",
            machine_number: machineNumber || "",
            machine_name: machineName || "",
            file_name: fileName || "",

            page: `/model-viewer/${machine_id || ""}/${fileName || ""}`,
            page_label: `${machineNumber || machine_id || ""} | 3D Model - ${fileName || ""}`,
            source: "portal",

            ...extra,
        }),
        [user, selectedSiteData, selectedSite, machine_id, machineNumber, machineName, fileName]
    );
    useEffect(() => {
        if (!modelLoaded || !selectedSiteData?.site_id || analyticsTracked3dOpen) return;

        trackEvent("model_3d_opened", {
            page: `/model-viewer/${machine_id}/${fileName}`,
            page_label: `${machineNumber || machine_id} | 3D Model - ${fileName}`,

            machine_id: machine_id || "",
            machine_number: machineNumber || "",
            machine_name: machineName || "",

            model_name: fileName || "",
            file_name: fileName || "",

            site_id: selectedSiteData?.site_id || "",
            site_number: selectedSite || "",
            customer_site_number: selectedSite || "",
            customer_id: selectedSiteData?.cus_id || "",
            legal_entity: selectedSiteData?.site_legal_entity || "",
        });
    }, [analyticsTracked3dOpen, modelLoaded, selectedSiteData?.site_id, getModelAnalyticsPayload]);
    const normalizePartMeta = (part = {}) => {
        const fallbackNumber = part.part_number || part.partNumber || part.product_number || null;
        let normalizedNumber = fallbackNumber ? String(fallbackNumber) : null;
        let normalizedProductId = part.product_id || part.productId || part.ProductID || null;
        let normalizedPrice = part.price;
        let normalizedPurchasable =
            part.purchasable !== undefined && part.purchasable !== null
                ? part.purchasable
                : null;

        if (normalizedNumber && (!normalizedProductId || normalizedPrice == null || normalizedPurchasable == null)) {
            const match = products?.find(
                (p) => p.product_number?.toString().trim() === normalizedNumber?.toString().trim()
            );
            if (match) {
                if (!normalizedProductId) normalizedProductId = match.product_id;
                if (normalizedPrice == null) normalizedPrice = getSitePrice(match);
                if (normalizedPurchasable == null) normalizedPurchasable = match.purchasable;
            }
        }

        return {
            ...part,
            part_number: normalizedNumber,
            product_id: normalizedProductId,
            price: normalizedPrice,
            purchasable: normalizedPurchasable,
        };
    };

    // Add to Cart
    const addToCart = (part) => {
        if (!canShowAddToCart) return;
        const effectivePart = getEffectiveProductDisplay(part, {
            machineId: machine_id,
            currency: selectedSiteCurrency,
        });
        const normalized = normalizePartMeta(effectivePart);
        if (!normalized.part_number) return;
        trackEvent("model_3d_add_to_cart", getModelAnalyticsPayload({
            action: "add_to_cart",
            part_number: normalized.part_number || "",
            product_id: normalized.product_id || "",
            price: normalized.price || "",
            purchasable: normalized.purchasable,
        }));

        setCartItems((prev) => {
            const exists = prev.find((item) => item.part_number === normalized.part_number);
            if (exists) return prev; // prevent duplicates

            return [
                ...prev,
                {
                    ...normalized,
                    qty: normalized.qty ?? 1,
                },
            ];
        });
    };

    // Add to Request List
    const addToRequestList = (part) => {
        if (!canShowAddToRequestList) return;
        const effectivePart = getEffectiveProductDisplay(part, {
            machineId: machine_id,
            currency: selectedSiteCurrency,
        });
        const normalized = normalizePartMeta(effectivePart);
        if (!normalized.part_number) return;

        setRequestItems((prev) => {
            const exists = prev.find((item) => item.part_number === normalized.part_number);
            if (exists) return prev;

            return [...prev, normalized];
        });
    };

    // Remove item
    const removeCartItem = (part_number) =>
        setCartItems(prev => prev.filter(item => item.part_number !== part_number));

    const removeRequestItem = (part_number) =>
        setRequestItems(prev => prev.filter(item => item.part_number !== part_number));


    // Quantity handlers
    const incrementQty = (part_number) =>
        setCartItems(prev =>
            prev.map(item =>
                item.part_number === part_number
                    ? { ...item, qty: item.qty + 1 }
                    : item
            )
        );

    const decrementQty = (part_number) =>
        setCartItems(prev =>
            prev.map(item =>
                item.part_number === part_number && item.qty > 1
                    ? { ...item, qty: item.qty - 1 }
                    : item
            )
        );

    // Check if part is in cart or request list
    const isInCart = (pn) => cartItems.some(i => i.part_number === pn);
    const isInRequest = (pn) => requestItems.some(i => i.part_number === pn);



    const normalizeRsplType = (value) => String(value || "").trim().toLowerCase();
    const normalizeRsplCode = (value) => String(value || "").trim().toUpperCase();
    const normalizeRsplList = (value) => {
        if (!value) return null;
        const parsed = Array.isArray(value)
            ? value
            : (() => {
                try {
                    return JSON.parse(value);
                } catch {
                    return null;
                }
            })();
        if (!Array.isArray(parsed)) return null;
        const sanitized = parsed.filter(
            (item) =>
                item &&
                typeof item.value === "string" &&
                Array.isArray(item.key) &&
                item.key.every((entry) => typeof entry === "string")
        );
        return sanitized.length > 0 ? sanitized : null;
    };

    const resolvedRsplList = useMemo(() => {
        return normalizeRsplList(optionKeys?.rsplList) || RSPL_LIST || [];
    }, [optionKeys?.rsplList]);

    const rsplTypeLabelMap = useMemo(() => {
        const map = new Map();
        (resolvedRsplList || []).forEach(({ value }) => {
            if (!value || typeof value !== "string") return;
            const key = normalizeRsplType(value);
            if (!map.has(key)) map.set(key, value);
        });
        return map;
    }, [resolvedRsplList]);

    const rsplCodeToTypeKey = useMemo(
        () =>
            Object.fromEntries(
                (resolvedRsplList || []).flatMap(({ key, value }) =>
                    key.map((k) => [normalizeRsplCode(k), normalizeRsplType(value)])
                )
            ),
        [resolvedRsplList]
    );

    const applyRsplTypeMetadata = React.useCallback(
        (machineProducts = []) => {
            return (machineProducts || []).map((product) => {
                const normalizedRsplCode = normalizeRsplCode(product?.product_rspl_code);
                const productTypeKey = rsplCodeToTypeKey[normalizedRsplCode] || null;
                const productTypeLabel = productTypeKey
                    ? (rsplTypeLabelMap.get(productTypeKey) || productTypeKey)
                    : t('UI_TEXT.OTHER');
                const imageUrl = resolveProductCardImageUrl(product);

                return {
                    ...product,
                    ProductType: productTypeLabel,
                    ProductTypeKey: productTypeKey,
                    productImageURL: imageUrl,
                };
            });
        },
        [rsplCodeToTypeKey, rsplTypeLabelMap, t]
    );

    // -------- part menu drag handlers --------
    const handlePartMenuMouseDown = (e) => {
        if (e.button !== 0) return; // left-click only
        setIsDraggingPartMenu(true);
        dragStartRef.current = {
            mouseX: e.clientX,
            mouseY: e.clientY,
            dx: partMenuDelta.x,
            dy: partMenuDelta.y,
        };
    };

    const handleMultiPartMenuMouseDown = (e) => {
        if (e.button !== 0) return;
        setIsDraggingMultiPartMenu(true);
        multiDragStartRef.current = {
            mouseX: e.clientX,
            mouseY: e.clientY,
            dx: multiPartMenuDelta.x,
            dy: multiPartMenuDelta.y,
        };
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDraggingPartMenu) {
                const { mouseX, mouseY, dx, dy } = dragStartRef.current;
                const newDx = dx + (e.clientX - mouseX);
                const newDy = dy + (e.clientY - mouseY);

                setPartMenuDelta({
                    x: newDx,
                    y: newDy,
                });
            }

            if (isDraggingMultiPartMenu) {
                const { mouseX, mouseY, dx, dy } = multiDragStartRef.current;
                const newDx = dx + (e.clientX - mouseX);
                const newDy = dy + (e.clientY - mouseY);

                setMultiPartMenuDelta({
                    x: newDx,
                    y: newDy,
                });
            }
        };

        const handleMouseUp = () => {
            if (isDraggingPartMenu) {
                setIsDraggingPartMenu(false);
            }
            if (isDraggingMultiPartMenu) {
                setIsDraggingMultiPartMenu(false);
            }
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDraggingPartMenu, isDraggingMultiPartMenu, partMenuDelta.x, partMenuDelta.y, multiPartMenuDelta.x, multiPartMenuDelta.y]);

    // --------- data fetching ---------

    useEffect(() => {
        if (!isEmpty(machine_id) && selectedSiteData?.site_id && selectedSite) {
            const key = `${machine_id}-${selectedSiteData.site_id}-${selectedSite}`;

            // Prevent repeated fetches for the same machine/site combo
            if (lastFetchKeyRef.current === key) return;

            lastFetchKeyRef.current = key;
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [machine_id, selectedSiteData?.site_id, selectedSite]);

    const fetchData = async () => {
        if (isFatalError) return; //   DO NOT START LOADING AGAIN

        setLoading(true);
        setDataReady(false);
        setError(null);
        setPercentage(0);
        setLoadingStep(0);
        setModelLoaded(false);


        const productsReqBody = {
            machine_id: machine_id,
            site_number: selectedSite,
        };

        try {
            const [machineResponse, productsResponse] = await Promise.all([
                getMachineByNumberOrId({
                    machine_id,
                    site_id: selectedSiteData?.site_id,
                }),
                getProductsByMachineId(productsReqBody),
            ]);

            let hadError = false;

            // machine
            if (machineResponse?.status && machineResponse?.data?.length) {
                const machineData = machineResponse.data[0];
                setMachine(machineData);
                setMachineName(machineData.machine_name);
                setMachineNumber(machineData.machine_number);
            } else {
                hadError = true;
                setMachine(null);
                setError('Failed fetching machine details.');
            }

            // products
            if (productsResponse?.status) {
                const { products: machineProducts, bomTree } =
                    normalizeMachineProductsResponse(productsResponse);

                const productsWithType = applyRsplTypeMetadata(machineProducts);


                setBomTreeData(bomTree || []);
                setProducts(productsWithType);
            } else {
                hadError = true;
                setBomTreeData([]);
                setError((prev) => prev || 'Failed to fetch products.');
            }

            if (hadError) {
                setLoading(false);
            } else {
                setDataReady(true);
            }
        } catch (err) {
            console.error('Error fetching machine or product details', err);
            setMachine(null);
            setBomTreeData([]);
            setProducts([]);
            setError('Failed fetching machine/products details.');
            setLoading(false);
        }
    };

    useEffect(() => {
        setProducts((prev) => {
            if (!Array.isArray(prev) || prev.length === 0) return prev;
            return applyRsplTypeMetadata(prev);
        });
    }, [applyRsplTypeMetadata]);

    const productTypes = useMemo(() => {
        const labels = (resolvedRsplList || [])
            .map((item) => String(item?.value || "").trim())
            .filter(Boolean);
        return Array.from(new Set(labels));
    }, [resolvedRsplList]);


    const productsByType = useMemo(() => {
        const map = {};
        productTypes.forEach((label) => {
            const key = normalizeRsplType(label);
            if (key) map[key] = [];
        });

        products.forEach((p) => {
            const key = p.ProductTypeKey;
            if (key && map[key]) {
                map[key].push(p);
            }
        });

        return map;
    }, [products, productTypes]);


    const [innerTab, setInnerTab] = useState(t('MENU_ITEMS.ALL'));

    useEffect(() => {
        if (productTypes.length > 0) {
            setInnerTab(t('MENU_ITEMS.ALL'));
        } else {
            setInnerTab("");
        }
    }, [productTypes]);



    const isFatalError = error === "Model Not Found";

    const normalizePartToken = (value) =>
        String(value ?? "")
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "");

    const getProductRefsForProduct = (productOrNumber) => {
        if (!assemblyIndex || !assemblyIndex.nodeById) return [];

        const product =
            typeof productOrNumber === "object" && productOrNumber !== null
                ? productOrNumber
                : { product_number: productOrNumber };

        const refs = new Set();
        const partNumberRaw = String(product?.product_number || "").trim();
        const partNameRaw = String(product?.product_name || "").trim();
        const productIdRaw =
            product?.product_id !== undefined && product?.product_id !== null
                ? String(product.product_id).trim()
                : "";

        const variantSet = new Set(
            [partNumberRaw, partNameRaw]
                .filter(Boolean)
                .flatMap((value) => [value, value.toLowerCase(), normalizePartToken(value)])
                .filter(Boolean)
        );

        assemblyIndex.nodeById.forEach((node) => {
            if (!node) return;
            if (node.productRef === null || node.productRef === undefined) return;

            const hasGeometry = Array.isArray(node.objects) && node.objects.length > 0;
            if (!hasGeometry) return;

            const nodeNameRaw = String(node.name || "").trim();
            const nodeNameLower = nodeNameRaw.toLowerCase();
            const nodeNameNormalized = normalizePartToken(nodeNameRaw);

            const matchesByName = Array.from(variantSet).some((candidate) => {
                const c = String(candidate || "");
                if (!c) return false;
                const lower = c.toLowerCase();
                return (
                    nodeNameLower === lower ||
                    nodeNameLower.startsWith(lower) ||
                    nodeNameNormalized === c ||
                    nodeNameNormalized.startsWith(c)
                );
            });

            if (matchesByName) {
                refs.add(String(node.productRef));
            }
        });

        if (refs.size === 0 && assemblyIndex.productRefToNodeIds) {
            const fallbackKeys = new Set(
                [partNumberRaw, productIdRaw]
                    .filter(Boolean)
                    .flatMap((value) => [
                        value,
                        value.toLowerCase(),
                        value.replace(/^#+/, ''),
                        normalizePartToken(value),
                    ])
            );

            fallbackKeys.forEach((key) => {
                const nodeIds = assemblyIndex.productRefToNodeIds.get(String(key));
                if (!nodeIds || nodeIds.length === 0) return;
                nodeIds.forEach((id) => {
                    const node = assemblyIndex.nodeById.get(id);
                    const hasGeometry = Array.isArray(node?.objects) && node.objects.length > 0;
                    if (!hasGeometry) return;
                    if (node.productRef !== null && node.productRef !== undefined) {
                        refs.add(String(node.productRef));
                    }
                });
            });
        }

        const preferredRaw = String(partNumberRaw || "").toLowerCase();
        const preferredId = String(productIdRaw || "").toLowerCase();
        return Array.from(refs).sort((a, b) => {
            const aKey = String(a).toLowerCase();
            const bKey = String(b).toLowerCase();
            const aPreferred = aKey === preferredRaw || aKey === preferredId;
            const bPreferred = bKey === preferredRaw || bKey === preferredId;
            if (aPreferred && !bPreferred) return -1;
            if (!aPreferred && bPreferred) return 1;
            return aKey.localeCompare(bKey);
        });
    };

    const getSelectableProductRefsForProduct = useCallback((productOrNumber) => {
        const refs = getProductRefsForProduct(productOrNumber);
        if (!modelLoaded || !sceneRef.current?.canSelectAssemblyNode) return refs;
        return refs.filter((ref) =>
            sceneRef.current?.canSelectAssemblyNode?.(String(ref))
        );
    }, [modelLoaded, assemblyIndex]);

    const resolvePrimaryAssemblyNodeForProductRef = useCallback((productRef) => {
        const refKey = String(productRef ?? '').trim();
        if (!assemblyIndex || !refKey) return null;
        const nodeIds = assemblyIndex.productRefToNodeIds?.get(refKey) ||
            assemblyIndex.productRefToNodeIds?.get(refKey.replace(/^#+/, '')) || [];
        for (const nodeId of nodeIds) {
            const node = assemblyIndex.nodeById?.get(nodeId);
            if (node && Array.isArray(node.objects) && node.objects.length > 0) {
                return node;
            }
        }
        return null;
    }, [assemblyIndex]);

    const getOccurrenceCountForProductRef = useCallback((productRef) => {
        const refKey = String(productRef ?? '').trim();
        if (!assemblyIndex || !refKey) return 0;
        const nodeIds = assemblyIndex.productRefToNodeIds?.get(refKey) ||
            assemblyIndex.productRefToNodeIds?.get(refKey.replace(/^#+/, '')) || [];
        const uniqueObjectUuids = new Set();
        nodeIds.forEach((nodeId) => {
            const node = assemblyIndex.nodeById?.get(nodeId);
            if (!node || !Array.isArray(node.objects)) return;
            node.objects.forEach((obj) => {
                if (obj?.uuid) uniqueObjectUuids.add(obj.uuid);
            });
        });
        return uniqueObjectUuids.size;
    }, [assemblyIndex]);

    const product3DMap = useMemo(() => {
        const map = {};
        if (!products || !assemblyIndex) return map;

        products.forEach((p) => {
            map[p.product_number] =
                getSelectableProductRefsForProduct(p).length > 0;
        });

        return map;
    }, [products, assemblyIndex, getSelectableProductRefsForProduct]);

    const allAssemblyProductRefs = useMemo(() => {
        if (!assemblyIndex?.nodeById) return [];

        const refs = new Set();
        assemblyIndex.nodeById.forEach((node) => {
            if (!node?.productRef || !Array.isArray(node.objects) || node.objects.length === 0) {
                return;
            }
            if (sceneRef.current?.canSelectAssemblyNode?.(String(node.productRef)) === false) {
                return;
            }
            refs.add(String(node.productRef));
        });
        return Array.from(refs);
    }, [assemblyIndex, modelLoaded]);


    const handleRSPLPartClick = (product) => {
        setSelectedPart(null);
        clearSelectionActionToggles();
        setSelectedParts(null);
        setIsolateSelectionToggle(false);
        setActiveRsplPartNumber(product.product_number);
        setSelectVisibleActive(false);
        setModelVisible(true);
        setNon3DMessageVisible(false);
        setNon3DPartNumber("");

        // A previous non-3D selection may have hidden the complete scene with
        // emptyMode="hideAll". Restore the scene before processing the next
        // RSPL card so a valid 3D part cannot inherit the blank view.
        sceneRef.current?.showOnlyProductRefs?.([], {
            emptyMode: "showAll",
            focus: false,
        });

        const selectedRefs = getSelectableProductRefsForProduct(product);
        const in3D = selectedRefs.length > 0;

        setShowPartMenu(true);
        setShowMultiPartMenu(false);
        setPartMenuData({
            part_name: product.product_name,
            part_number: product.product_number,
            part_type: product.ProductType,
            product_id: product.product_id,
            price: getSitePrice(product),
            purchasable: product.purchasable,
            isOrderable: true,
            availableIn3D: in3D,
        });

        trackEvent("model_3d_part_clicked", getModelAnalyticsPayload({
            action: "part_clicked",
            part_number: product.product_number || "",
            part_name: product.product_name || "",
            product_id: product.product_id || "",
            product_type: product.ProductType || "",
            available_in_3d: in3D,
        }));
        if (!in3D) {
            // Part exists in RSPL, but no 3D geometry in the assembly
            setNon3DPartNumber(product.product_number || "");
            keepPartMenuOnNextClearRef.current = true;
            sceneRef.current?.showOnlyProductRefs?.([], {
                emptyMode: "hideAll",
                focus: false,
            });
            sceneRef.current?.clearSelection?.();
            setModelVisible(false);
            setNon3DMessageVisible(true);
            return;
        }

        setModelVisible(true);
        setNon3DMessageVisible(false);
        setNon3DPartNumber("");
        keepPartMenuOnNextClearRef.current = false;

        if (!assemblyIndex || !sceneRef.current?.selectAssemblyNode) return;
        const refs = selectedRefs.map((ref) => String(ref));
        const primaryRef = refs[0] || null;
        const primaryNode = resolvePrimaryAssemblyNodeForProductRef(primaryRef);
        const occurrenceCount = getOccurrenceCountForProductRef(primaryRef);
        const hasMultipleOccurrences = occurrenceCount > 1;

        // Make the selected part visible before selecting/isolation. This is
        // required after a non-3D card hid the entire model.
        sceneRef.current?.showOnlyProductRefs?.(refs, {
            emptyMode: "hideAll",
            focus: false,
        });

        setPartMenuData({
            part_name: product.product_name,
            part_number: product.product_number,
            part_type: product.ProductType,
            product_id: product.product_id,
            price: getSitePrice(product),
            purchasable: product.purchasable,
            isOrderable: true,
            availableIn3D: in3D,
            hasMultipleOccurrences,
            occurrenceCount: occurrenceCount || 1,
        });

        const didSelect = sceneRef.current?.selectAssemblySubtreeByProductRefs?.(refs, {
            primaryProductRef: primaryRef || refs[0],
            // Keep the complete assembly in view; selection highlighting
            // identifies the part without moving the camera off the model.
            focus: false,
            emitSelectionCallbacks: false,
            includeAllOccurrences: false,
        });

        if (didSelect) {
            setSelectedPart({
                name: product.product_name || primaryNode?.name || product.product_number || "Selected Part",
                uuid: `rspl-${product.product_number || primaryRef || Date.now()}`,
                type: "RSPLPart",
                userData: {},
                material: null,
                object3D: null,
                assemblyNode: primaryNode,
            });
            setSelectedParts([]);
        }

        if (!didSelect) {
            // Last-resort fallback: keep menu data, hide model content, and show non-3D message.
            setNon3DPartNumber(product.product_number || "");
            keepPartMenuOnNextClearRef.current = true;
            sceneRef.current?.showOnlyProductRefs?.([], {
                emptyMode: "hideAll",
                focus: false,
            });
            sceneRef.current?.clearSelection?.();
            setModelVisible(false);
            setNon3DMessageVisible(true);
            return;
        }

        setIsolateSelectionToggle(true);
    };

    const handleRsplTypeChange = (_event, nextType) => {
        // Changing the RSPL category must leave the previous part-isolation
        // state. Otherwise the visibility effect exits early and the scene
        // remains locked to the previously selected part.
        sceneRef.current?.clearSelection?.();
        setInnerTab(nextType);
        setSuspendRsplFilter(false);
        setIsolateSelectionToggle(false);
        setOccurrenceToggle(false);
        setSelectedPart(null);
        setSelectedParts([]);
        setShowPartMenu(false);
        setShowMultiPartMenu(false);
        setModelVisible(true);
        setNon3DMessageVisible(false);
        setNon3DPartNumber("");
    };

    const currentRsplProductRefs = useMemo(() => {
        if (!assemblyIndex || !rsplReady) return [];

        let targetProducts = [];
        if (innerTab === t('MENU_ITEMS.ALL')) {
            // The initial RSPL All view must represent the complete loaded 3D
            // assembly, not only API products that have an RSPL category.
            return allAssemblyProductRefs;
        } else {
            const key = normalizeRsplType(innerTab);
            const items = productsByType[key] ?? [];
            targetProducts.push(...items);
        }

        const refSet = new Set();
        targetProducts.forEach((p) => {
            if (!product3DMap[p.product_number]) return;
            const refs = getProductRefsForProduct(p);
            refs.forEach((r) => refSet.add(r));
        });

        return Array.from(refSet);
    }, [
        assemblyIndex,
        rsplReady,
        innerTab,
        productTypes,
        productsByType,
        product3DMap,
        allAssemblyProductRefs,
    ]);

    useEffect(() => {
        // Need model + assembly + RSPL parts + the helper from ModelScene
        if (!sceneRef.current || !assemblyIndex) return;
        if (machineViewMode !== "normal") return;

        // If not on RSPL tab or RSPL not ready -> show everything again
        if (tabValue !== 1 || !rsplReady) {
            sceneRef.current.showOnlyProductRefs?.([]);
            return;
        }

        if (suspendRsplFilter) {
            sceneRef.current.showOnlyProductRefs?.([]);
            return;
        }

        sceneRef.current.showOnlyProductRefs?.(currentRsplProductRefs, {
            emptyMode: "hideAll",
            focus: false,
        });
    }, [
        tabValue,
        rsplReady,
        assemblyIndex,
        currentRsplProductRefs,
        machineViewMode,
        suspendRsplFilter,
    ]);

    useEffect(() => {
        if (tabValue !== 1 || !rsplReady) return;
        if (suspendRsplFilter) return;
        if (!selectedPart) return;
        if (machineViewMode !== "normal") return;
        // The initial All view intentionally contains every selectable 3D
        // assembly node, including BOM parts classified as Other.
        if (innerTab === t('MENU_ITEMS.ALL')) return;

        // A real canvas selection is already visible; do not remove its menu
        // just because the RSPL product mapping is incomplete for that node.
        // Category filters still hide and clear synthetic/list selections.
        const selectedObject = selectedPart?.object3D;
        if (selectedObject) {
            let currentObject = selectedObject;
            let visible = true;
            while (currentObject) {
                if (currentObject.visible === false) {
                    visible = false;
                    break;
                }
                currentObject = currentObject.parent;
            }
            if (visible) return;
        }

        const selectedName = selectedPart?.assemblyNode?.name || selectedPart?.name || "";
        const matched = findMatchingProductForPart(
            selectedName,
            selectedPart?.assemblyNode?.productRef
        );
        const selectedRefs = new Set();
        const nodeProductRef = selectedPart?.assemblyNode?.productRef;
        if (nodeProductRef !== undefined && nodeProductRef !== null) {
            selectedRefs.add(String(nodeProductRef));
        }
        if (matched) {
            const matchedRefs = getProductRefsForProduct(matched);
            matchedRefs.forEach((ref) => selectedRefs.add(String(ref)));
        }
        if (selectedRefs.size === 0) return;

        const normalizeRef = (value) => String(value ?? '').trim().replace(/^#+/, '').toLowerCase();
        const refSet = new Set(currentRsplProductRefs.map(normalizeRef));
        const isVisibleInFilter = Array.from(selectedRefs).some((r) => refSet.has(normalizeRef(r)));

        if (!isVisibleInFilter) {
            sceneRef.current?.clearSelection?.();
            setSelectedPart(null);
            clearSelectionActionToggles();
            setSelectedParts(null);
            setShowPartMenu(false);
            setShowMultiPartMenu(false);
        }
    }, [
        tabValue,
        rsplReady,
        selectedPart,
        innerTab,
        currentRsplProductRefs,
        machineViewMode,
        suspendRsplFilter,
    ]);




    useEffect(() => {
        if (cartItems.length > 0 || requestItems.length > 0) {
            setCartOpen(true);
        }
    }, [cartItems, requestItems]);



    useEffect(() => {
        if (!rsplSearchTerm) {
            setRsplSearchResults([]);
            setRsplSearchIndex(0);
            return;
        }

        const lower = rsplSearchTerm.toLowerCase();
        const matches = products.filter(
            (p) =>
                p.ProductTypeKey &&
                (p.product_name.toLowerCase().includes(lower) ||
                    p.product_number.toLowerCase().includes(lower))
        );

        setRsplSearchResults(matches);
        setRsplSearchIndex(0);   // reset index
        const trackingKey = `${rsplSearchTerm.toLowerCase()}|${machine_id}|${matches.length}`;
        if (lastTrackedSearchRef.current !== trackingKey) {
            lastTrackedSearchRef.current = trackingKey;
            trackSearchPerformed({
                searchTerm: rsplSearchTerm,
                searchSource: "3d_model_rspl_parts",
                pageName: "3D Model",
                resultCount: matches.length,
                machine_id,
                machine_number: machineNumber,
            });
        }

        if (matches.length > 0) {
            const match = matches[0];

            const typeToOpen =
                match.ProductTypeKey ? (match.ProductType || t('MENU_ITEMS.ALL')) : t('MENU_ITEMS.ALL');
            setInnerTab(typeToOpen);

            setTimeout(() => {
                const el = document.getElementById(`rspl-${match.product_number}`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 150);
        }
    }, [machineNumber, machine_id, products, rsplSearchTerm]);


    const handleRsplSearchSubmit = () => {
        if (rsplSearchResults.length === 0) return;

        const nextIndex = (rsplSearchIndex + 1) % rsplSearchResults.length;
        setRsplSearchIndex(nextIndex);

        const match = rsplSearchResults[nextIndex];

        // Switch tab
        const typeToOpen = match.ProductTypeKey ? (match.ProductType || t('MENU_ITEMS.ALL')) : t('MENU_ITEMS.ALL');
        setInnerTab(typeToOpen);

        // Scroll to card
        setTimeout(() => {
            const el = document.getElementById(`rspl-${match.product_number}`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 150);
    };




    // ---------- loading progress ----------

    const allReady = dataReady && modelLoaded;

    const [modelFilesExist, setModelFilesExist] = useState(null); // null = unknown, true/false later
    const [loadingStopped, setLoadingStopped] = useState(false);

    const checkModelFilesExist = async () => {
        const glbURL = `${SERVER_BASE_URL}/stock-models/GLB/Models/${fileName}.glb`;
        const bomURL = `${SERVER_BASE_URL}/stock-models/GLB/Boms/${fileName}.json`;

        try {
            const [glbRes, bomRes] = await Promise.all([
                fetch(glbURL, { method: "HEAD", headers: authHeader() }),
                fetch(bomURL, { method: "HEAD", headers: authHeader() }),
            ]);

            const filesOk = glbRes.ok && bomRes.ok;
            setModelFilesExist(filesOk);
            return filesOk;
        } catch {
            setModelFilesExist(false);
            return false;
        }
    };



    useEffect(() => {
        if (!fileName) return;

        const verify = async () => {
            if (isFatalError) return;  // stop entirely

            setLoading(true);
            setLoadingStopped(false);


            const exists = await checkModelFilesExist();

            if (!exists) {
                // STOP ALL LOADING
                setError("Model Not Found");
                setLoadingStopped(true);
                setLoading(false);
                setPercentage(0);
            }
        };

        verify();
    }, [fileName]);

    useEffect(() => {
        if (!loading || loadingStopped || isFatalError) return;

        const interval = setInterval(() => {
            setPercentage((prev) => {
                if (prev >= 100) return 100;

                if (progressPhase === "loading") {
                    if (prev < 25) return prev + 8;
                    if (prev < 60) return prev + 4;
                    if (prev < 85) return prev + 2;
                    return Math.min(prev + 1, 96);
                }

                if (progressPhase === "finishing") {
                    if (prev < 96) return prev + 6;
                    return Math.min(prev + 12, 100);
                }

                return prev;
            });
        }, 110);

        return () => clearInterval(interval);
    }, [loading, loadingStopped, progressPhase]);



    useEffect(() => {
        if (isFatalError) return;
        if (allReady && percentage >= 100) {
            setLoading(false);
        }

    }, [allReady, percentage]);

    useEffect(() => {
        if (percentage < 25) {
            setLoadingStep(0);
        } else if (percentage < 60) {
            setLoadingStep(1);
        } else if (percentage < 90) {
            setLoadingStep(2);
        } else {
            setLoadingStep(3);
        }
    }, [percentage]);

    useEffect(() => {
        if (dataReady && modelLoaded) {
            setProgressPhase("finishing");
        }
    }, [dataReady, modelLoaded]);

    // ---------- assembly / tree wiring ----------

    useEffect(() => {
        if (!assemblyIndex) return;

        const { nodeById, rootId } = assemblyIndex;
        const vis = {};
        nodeById.forEach((_node, id) => {
            vis[id] = true;
        });
        setNodeVisibility(vis);
        setExpanded([rootId]);
    }, [assemblyIndex]);

    const level1SubAssemblies = useMemo(() => {
        if (!assemblyIndex) return [];
        const { nodeById, parentById, rootId } = assemblyIndex;
        const result = [];
        nodeById.forEach((node, id) => {
            const hasChildren =
                Array.isArray(node?.children) && node.children.length > 0;
            if (parentById.get(id) === rootId && hasChildren) {
                result.push({ id, node });
            }
        });
        return result;
    }, [assemblyIndex]);

    const filteredSubAssemblies = useMemo(() => {
        if (!subSearchTerm) return level1SubAssemblies;
        const lower = subSearchTerm.trim().toLowerCase();
        return (level1SubAssemblies || []).filter(({ node }) => {
            const nodeName = (node?.name || "").trim().toLowerCase();
            const productRef =
                node?.productRef !== undefined && node?.productRef !== null
                    ? String(node.productRef).trim().toLowerCase()
                    : "";

            const matchedProduct =
                products.find((p) => {
                    const num = p.product_number?.trim().toLowerCase();
                    const pname = p.product_name?.trim().toLowerCase();
                    const pid =
                        p.product_id !== undefined && p.product_id !== null
                            ? String(p.product_id).trim().toLowerCase()
                            : "";

                    return (
                        (productRef && productRef === num) ||
                        (productRef && productRef === pid) ||
                        nodeName === num ||
                        nodeName.startsWith(num || "") ||
                        (num && nodeName.includes(num)) ||
                        nodeName === pname ||
                        (pname && nodeName.startsWith(pname)) ||
                        (pname && nodeName.includes(pname))
                    );
                }) || null;

            const haystacks = [
                node?.name,
                node?.productName,
                node?.productNumber,
                node?.product_name,
                node?.product_number,
                node?.productRef,
                matchedProduct?.product_name,
                matchedProduct?.product_number,
            ]
                .filter(Boolean)
                .map((value) => String(value).toLowerCase());

            return haystacks.some((value) => value.includes(lower));
        });
    }, [level1SubAssemblies, subSearchTerm, products]);

    const handleOccurrenceToggle = () => {
        if (!selectedPart) return;

        if (!occurrenceToggle) {
            // Expand selection to all occurrences
            sceneRef.current?.selectAllOccurrences?.();
        } else {
            // Revert to single occurrence
            sceneRef.current?.selectSingleOccurrence?.(selectedPart.uuid);
        }
        setOccurrenceToggle(!occurrenceToggle);
    };

    const clearSelectionActionToggles = () => {
        setPartTransparencyToggle(false);
        setIsolateSelectionToggle(false);
    };

    const getTransparencySelectionKey = (partMeta) => {
        const productRef = partMeta?.assemblyNode?.productRef;
        if (productRef !== null && productRef !== undefined && String(productRef).trim()) {
            return `p:${String(productRef).trim()}`;
        }

        const uuid = partMeta?.uuid;
        if (uuid && String(uuid).trim()) {
            return `u:${String(uuid).trim()}`;
        }

        return null;
    };

    const getEffectivePartTransparency = (partMeta, overrides = partTransparencyOverrides) => {
        const key = getTransparencySelectionKey(partMeta);
        if (!key) return machineTransparentToggle;
        if (Object.prototype.hasOwnProperty.call(overrides, key)) {
            return overrides[key];
        }
        return machineTransparentToggle;
    };

    const buildNodeProductSearchCandidates = (nodeLike, fallbackName = '', fallbackProductRef = null) => {
        const values = [
            typeof nodeLike === 'object' && nodeLike !== null ? nodeLike?.name : null,
            typeof nodeLike === 'object' && nodeLike !== null ? nodeLike?.productName : null,
            typeof nodeLike === 'object' && nodeLike !== null ? nodeLike?.productNumber : null,
            typeof nodeLike === 'object' && nodeLike !== null ? nodeLike?.product_name : null,
            typeof nodeLike === 'object' && nodeLike !== null ? nodeLike?.product_number : null,
            typeof nodeLike === 'object' && nodeLike !== null ? nodeLike?.description : null,
            typeof nodeLike === 'string' ? nodeLike : fallbackName,
        ]
            .filter(Boolean)
            .map((value) => String(value).trim())
            .filter(Boolean);

        const refs = [
            typeof nodeLike === 'object' && nodeLike !== null ? nodeLike?.productRef : null,
            fallbackProductRef,
        ]
            .filter((value) => value !== undefined && value !== null)
            .map((value) => String(value).trim())
            .filter(Boolean);

        return {
            names: Array.from(new Set(values.map((value) => value.toLowerCase()))),
            refs: Array.from(new Set(refs.map((value) => value.toLowerCase()))),
        };
    };

    const findMatchingProductForPart = (assemblyNodeName, productRef = null) => {
        if (!products?.length) return null;

        const { names, refs } = buildNodeProductSearchCandidates(
            assemblyNodeName,
            typeof assemblyNodeName === 'string' ? assemblyNodeName : '',
            productRef
        );

        return (
            products.find((p) => {
                const num = p.product_number?.trim().toLowerCase();
                const pname = p.product_name?.trim().toLowerCase();
                const pid = p.product_id !== undefined && p.product_id !== null
                    ? String(p.product_id).trim().toLowerCase()
                    : '';

                return (
                    refs.some((ref) => {
                        const normalizedRef = String(ref || '').replace(/^#+/, '');
                        const normalizedNum = String(num || '').replace(/^#+/, '');
                        const normalizedPid = String(pid || '').replace(/^#+/, '');
                        return normalizedRef && (normalizedRef === normalizedNum || normalizedRef === normalizedPid);
                    }) ||
                    names.some((name) => (
                        name === num ||
                        name.startsWith(num) ||
                        (num && name.includes(num)) ||
                        name === pname ||
                        (pname && name.startsWith(pname)) ||
                        (pname && name.includes(pname))
                    ))
                );
            }) || null
        );
    };

    const canProductBeActioned = useCallback((product) => {
        if (!product) return false;

        const productNumber = String(product?.product_number || "").trim();
        const hasIdentity =
            product?.product_id !== undefined &&
            product?.product_id !== null &&
            String(product.product_id).trim() !== "";

        if (!productNumber || !hasIdentity) {
            return false;
        }
        return true;
    }, []);

    const isSameResolvedProduct = useCallback((left, right) => {
        if (!left || !right) return false;

        const leftId =
            left?.product_id !== undefined && left?.product_id !== null
                ? String(left.product_id).trim()
                : "";
        const rightId =
            right?.product_id !== undefined && right?.product_id !== null
                ? String(right.product_id).trim()
                : "";

        if (leftId && rightId) {
            return leftId === rightId;
        }

        const leftNumber = String(left?.product_number || "").trim().toLowerCase();
        const rightNumber = String(right?.product_number || "").trim().toLowerCase();
        return !!leftNumber && !!rightNumber && leftNumber === rightNumber;
    }, []);

    const getAssemblyParentNode = useCallback((node) => {
        if (!node || !assemblyIndex || !node._id) return null;
        const parentId = assemblyIndex.parentById?.get(node._id);
        return parentId ? assemblyIndex.nodeById?.get(parentId) || null : null;
    }, [assemblyIndex]);

    const resolveNearestOrderableSelectionNode = useCallback((node) => {
        if (!node) {
            return {
                targetNode: null,
                matchedProduct: null,
                redirected: false,
            };
        }

        const originalMatch = findMatchingProductForPart(node, node?.productRef);

        let current = node;
        while (current) {
            const match = findMatchingProductForPart(current, current?.productRef);
            const hasGeometry =
                Array.isArray(current?.objects) && current.objects.length > 0;

            if (match && hasGeometry && canProductBeActioned(match)) {
                let highestSameProductNode = current;
                let parent = getAssemblyParentNode(current);

                while (parent) {
                    const parentHasGeometry =
                        Array.isArray(parent?.objects) && parent.objects.length > 0;
                    const parentMatch = findMatchingProductForPart(parent, parent?.productRef);

                    if (
                        parentHasGeometry &&
                        canProductBeActioned(parentMatch) &&
                        isSameResolvedProduct(parentMatch, match)
                    ) {
                        highestSameProductNode = parent;
                        parent = getAssemblyParentNode(parent);
                        continue;
                    }

                    break;
                }

                return {
                    targetNode: highestSameProductNode,
                    matchedProduct: match,
                    redirected: highestSameProductNode !== node,
                };
            }

            current = getAssemblyParentNode(current);
        }

        return {
            targetNode: node,
            matchedProduct: originalMatch,
            redirected: false,
        };
    }, [canProductBeActioned, findMatchingProductForPart, getAssemblyParentNode, isSameResolvedProduct]);

    const resolveTreeNodeMeta = (node) => {
        const matchedProduct = findMatchingProductForPart(node, node?.productRef);
        const productRefFallback =
            node?.productRef !== undefined && node?.productRef !== null
                ? String(node.productRef)
                : '';
        const partName =
            matchedProduct?.product_name ||
            node?.name ||
            productRefFallback ||
            '(no name)';
        const partNumber = matchedProduct?.product_number || productRefFallback || '';

        return {
            partName,
            partNumber,
            description: node?.description ? String(node.description) : '',
        };
    };

    const expandAllTreeNodes = () => {
        if (!assemblyIndex) return;
        setExpanded(Array.from(assemblyIndex.nodeById.keys()));
    };

    const collapseAllTreeNodes = () => {
        if (!assemblyIndex?.rootId) {
            setExpanded([]);
            return;
        }
        setExpanded([assemblyIndex.rootId]);
    };

    const scrollPartsTreeToTop = () => {
        partsTreeScrollRef.current?.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const scrollSubAssembliesToTop = () => {
        subAssembliesScrollRef.current?.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const scrollRsplToTop = () => {
        rsplScrollRef.current?.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };



    const handlePartSelected = (partMeta) => {
        setOccurrenceToggle(false);

        if (!partMeta) {
            if (isolateSelectionToggle) {
                setShowPartMenu(false);
                setShowMultiPartMenu(false);
                return;
            }
            setSelectedPart(null);
            clearSelectionActionToggles();
            setSelectedNodeId(null);
            if (keepPartMenuOnNextClearRef.current) {
                keepPartMenuOnNextClearRef.current = false;
                return;
            }
            setPartMenuData(null);
            setShowPartMenu(false);
            return;
        }

        // A canvas miss can be reported by React Three Fiber during the same
        // pointer interaction as a mesh selection. Keep the selection timestamp
        // so the miss handler cannot immediately undo a valid 3D selection.
        lastModelSelectionAtRef.current = Date.now();

        const {
            targetNode,
            matchedProduct,
            occurrenceCount,
            hasMultipleOccurrences,
        } = buildSelectionDescriptorFromNode(
            partMeta.assemblyNode,
            partMeta.name || "Selected Part"
        );

        const resolvedPartMeta = {
            ...partMeta,
            name: targetNode?.name || partMeta.name,
            assemblyNode: targetNode || partMeta.assemblyNode,
        };

        setSelectedPart(resolvedPartMeta);
        setPartTransparencyToggle(getEffectivePartTransparency(resolvedPartMeta));

        if (targetNode && targetNode._id) {
            setSelectedNodeId(targetNode._id);
        }

        const assemblyName = targetNode?.name || partMeta.name || "";
        const isOrderable = canProductBeActioned(matchedProduct);
        const resolvedPartType =
            matchedProduct?.ProductType || matchedProduct?.ProductTypeKey || "N/A";
        const effectiveTarget = getEffectiveProductTarget(matchedProduct, {
            machineId: machine_id,
            currency: selectedSiteCurrency,
        });
        const effectiveCurrencyKey = String(selectedSiteCurrency || "USD").toLowerCase();
        const effectiveDisplayPrice = Number(effectiveTarget.price) > 0
            ? getSitePrice({
                [`price_${effectiveCurrencyKey}`]: effectiveTarget.price,
            })
            : getSitePrice(matchedProduct);
        const partData = {
            part_name: (isOrderable ? matchedProduct?.product_name : null) || assemblyName || "Selected Part",
            part_number: isOrderable
                ? matchedProduct.product_number
                : (targetNode?.productRef != null ? String(targetNode.productRef) : null),
            part_type: isOrderable
                ? resolvedPartType
                : null,
            url: isOrderable ? `/machines/${machine_id}/${matchedProduct.product_id}` : "",
            price: isOrderable ? getSitePrice(matchedProduct) : 0,
            purchasable: isOrderable ? matchedProduct.purchasable : 0,
            product_id: isOrderable ? matchedProduct.product_id : null,
            hasMultipleOccurrences,
            occurrenceCount,
            isOrderable: isOrderable,
            alternative_item_number: matchedProduct?.alternative_item_number,
            replacement: matchedProduct?.replacement,
            effective_product_id: effectiveTarget.product_id,
            effective_part_number: effectiveTarget.product_number,
            effective_price: effectiveDisplayPrice,
            effective_purchasable: effectiveTarget.purchasable,
        };


        // Update PartMenu UI
        setPartMenuData(partData);

        // Show menu
        setShowPartMenu(true);

        if (settings.transparentOnSelect && !getEffectivePartTransparency(resolvedPartMeta)) {
            const key = getTransparencySelectionKey(resolvedPartMeta);
            if (key) {
                setPartTransparencyOverrides((prev) => ({
                    ...prev,
                    [key]: true,
                }));
            }
        }
        if (settings.autoZoom) {
            sceneRef.current?.zoomToSelectionToggle?.();
        }

    };

    useEffect(() => {
        if (!selectedPart) {
            setPartTransparencyToggle(false);
            return;
        }

        const key = getTransparencySelectionKey(selectedPart);
        const next =
            key && Object.prototype.hasOwnProperty.call(partTransparencyOverrides, key)
                ? partTransparencyOverrides[key]
                : machineTransparentToggle;
        setPartTransparencyToggle(next);
    }, [selectedPart, partTransparencyOverrides, machineTransparentToggle]);

    useEffect(() => {
        const currentSelection = Array.isArray(selectedParts)
            ? selectedParts.filter(Boolean)
            : [];

        if (currentSelection.length > 1) {
            setMultiPartSelection(currentSelection);
            setShowMultiPartMenu(true);
            setShowPartMenu(false);
            return;
        }

        if (currentSelection.length <= 1) {
            setShowMultiPartMenu(false);
            if (currentSelection.length === 0) setMultiPartSelection([]);
        }
    }, [selectedParts]);

    const handleVisibilityChange = (productVisibility) => {
        if (!assemblyIndex) return;

        const { nodeById, parentById, productRefToNodeIds, rootId } = assemblyIndex;
        const productVis = productVisibility || {};

        // 1) Start with everything "not visible"
        const baseVis = {};
        nodeById.forEach((_node, id) => {
            baseVis[id] = false;
        });

        // 2) Apply visibility coming from productRef -> visible map
        Object.entries(productVis).forEach(([productRefStr, visible]) => {
            const nodeIds = productRefToNodeIds.get(productRefStr);
            if (!nodeIds) return;
            nodeIds.forEach((id) => {
                baseVis[id] = visible;
            });
        });

        // 3) Build a parent -> children map for the assembly tree
        const childrenByParent = new Map();
        parentById.forEach((parentId, id) => {
            if (!childrenByParent.has(parentId)) {
                childrenByParent.set(parentId, []);
            }
            childrenByParent.get(parentId).push(id);
        });

        const computed = { ...baseVis };

        // 4) Recursively compute visibility so parents follow children
        const computeForNode = (id) => {
            const node = nodeById.get(id);
            if (!node) return false;

            const childIds = childrenByParent.get(id);
            let childrenVisible = false;

            if (childIds && childIds.length) {
                childIds.forEach((childId) => {
                    if (computeForNode(childId)) {
                        childrenVisible = true;
                    }
                });
            }

            // Node is visible if it itself is visible OR any child is visible
            const selfVisible = baseVis[id];
            const result = selfVisible || childrenVisible;
            computed[id] = result;
            return result;
        };

        if (rootId) {
            computeForNode(rootId);
        }

        setNodeVisibility(computed);
    };


    // auto-expand ancestors when selectedNodeId changes
    useEffect(() => {
        if (!selectedNodeId || !assemblyIndex) return;

        const { parentById } = assemblyIndex;

        setExpanded((prev) => {
            const toExpand = new Set(prev);
            let curr = selectedNodeId;
            while (parentById.has(curr)) {
                const parentId = parentById.get(curr);
                toExpand.add(parentId);
                curr = parentId;
            }
            return Array.from(toExpand);
        });
    }, [selectedNodeId, assemblyIndex]);

    // scroll to the selected tree item once expanded
    useEffect(() => {
        if (!selectedNodeId) return;

        const el = treeItemRefs.current.get(selectedNodeId);
        if (el) {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }, [selectedNodeId, expanded]);

    useEffect(() => {
        if (!assemblyIndex) return;

        // Clear when search is empty
        if (!treeSearchTerm) {
            setTreeSearchMatches([]);
            setTreeSearchIndex(0);
            pendingTreeSearchScrollRef.current = null;
            return;
        }

        const lower = treeSearchTerm.toLowerCase();
        const matches = [];

        assemblyIndex.nodeById.forEach((node, id) => {
            const { partName, partNumber, description } = resolveTreeNodeMeta(node);
            const searchFields = [partName, partNumber, description]
                .filter(Boolean)
                .map((value) => String(value).toLowerCase());

            if (searchFields.some((value) => value.includes(lower))) {
                matches.push(id);
            }
        });

        setTreeSearchMatches(matches);
        setTreeSearchIndex(0);
    }, [treeSearchTerm, assemblyIndex, products]);

    useEffect(() => {
        if (!assemblyIndex || !treeSearchMatches.length) {
            pendingTreeSearchScrollRef.current = null;
            return;
        }

        const { parentById } = assemblyIndex;
        const activeId =
            treeSearchMatches[treeSearchIndex] || treeSearchMatches[0];

        // Ensure ancestors of active match are expanded
        setExpanded((prev) => {
            const toExpand = new Set(prev);
            let current = activeId;
            while (parentById.has(current)) {
                const parentId = parentById.get(current);
                toExpand.add(parentId);
                current = parentId;
            }
            return Array.from(toExpand);
        });
        pendingTreeSearchScrollRef.current = activeId;
    }, [treeSearchMatches, treeSearchIndex, assemblyIndex]);

    useEffect(() => {
        const activeId = pendingTreeSearchScrollRef.current;
        if (!activeId) return;

        const timer = setTimeout(() => {
            const el = treeItemRefs.current.get(activeId);
            if (!el) return;
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
            pendingTreeSearchScrollRef.current = null;
        }, 50);

        return () => clearTimeout(timer);
    }, [expanded, treeSearchMatches, treeSearchIndex]);

    const handleTreeSearchSubmit = () => {
        if (!treeSearchMatches.length) return;

        setTreeSearchIndex((prev) => {
            if (treeSearchMatches.length <= 1) return 0;
            return (prev + 1) % treeSearchMatches.length; // cycle
        });
    };

    const collectProductRefsFromNode = (node, targetSet) => {
        if (!node || !targetSet) return;

        if (node.productRef !== null && node.productRef !== undefined) {
            targetSet.add(String(node.productRef));
        }

        if (Array.isArray(node.children)) {
            node.children.forEach((child) =>
                collectProductRefsFromNode(child, targetSet)
            );
        }
    };

    const buildSelectionDescriptorFromNode = useCallback((node, fallbackLabel = "Selected Part") => {
        const { targetNode, matchedProduct } = resolveNearestOrderableSelectionNode(node);
        const effectiveNode = targetNode || node;
        const actionableProduct = canProductBeActioned(matchedProduct)
            ? matchedProduct
            : null;
        const refsToShow = new Set();

        collectProductRefsFromNode(effectiveNode, refsToShow);

        const refs = Array.from(refsToShow);
        const selectedNodeRef =
            effectiveNode?.productRef !== undefined && effectiveNode?.productRef !== null
                ? String(effectiveNode.productRef)
                : null;
        const primaryRef =
            selectedNodeRef && refs.includes(selectedNodeRef)
                ? selectedNodeRef
                : (refs[0] || selectedNodeRef);
        const occurrenceCount = getOccurrenceCountForProductRef(primaryRef);
        const fallbackOccurrenceCount = Array.isArray(effectiveNode?.objects)
            ? effectiveNode.objects.length
            : 0;
        const effectiveOccurrenceCount =
            occurrenceCount || fallbackOccurrenceCount || 1;
        const resolvedPartType =
            actionableProduct?.ProductType || actionableProduct?.ProductTypeKey || "N/A";

        return {
            targetNode: effectiveNode,
            matchedProduct: actionableProduct,
            refs,
            primaryRef,
            hasMultipleOccurrences: effectiveOccurrenceCount > 1,
            occurrenceCount: effectiveOccurrenceCount,
            partMenuData: {
                part_name:
                    actionableProduct?.product_name ||
                    effectiveNode?.name ||
                    fallbackLabel,
                part_number: actionableProduct?.product_number || null,
                part_type: actionableProduct ? resolvedPartType : null,
                product_id: actionableProduct?.product_id || null,
                price: actionableProduct ? getSitePrice(actionableProduct) : null,
                purchasable: actionableProduct?.purchasable ?? 0,
                isOrderable: !!actionableProduct,
                availableIn3D: refs.length > 0,
                hasMultipleOccurrences: effectiveOccurrenceCount > 1,
                occurrenceCount: effectiveOccurrenceCount,
                alternative_item_number: actionableProduct?.alternative_item_number,
                replacement: actionableProduct?.replacement,
            },
        };
    }, [canProductBeActioned, getOccurrenceCountForProductRef, getSitePrice, resolveNearestOrderableSelectionNode]);

    const applyVisibilityForProductRefs = (productRefs = [], visible) => {
        if (!assemblyIndex || !sceneRef.current) return false;
        const refs = Array.from(
            new Set((Array.isArray(productRefs) ? productRefs : [productRefs]).map((r) => String(r)).filter(Boolean))
        );
        if (!refs.length) return false;

        sceneRef.current?.setAssemblyNodesVisibility?.(refs, visible);

        const { productRefToNodeIds } = assemblyIndex;
        setNodeVisibility((prev) => {
            const next = { ...prev };
            refs.forEach((ref) => {
                const nodeIds = productRefToNodeIds.get(String(ref));
                if (!nodeIds) return;
                nodeIds.forEach((id) => {
                    next[id] = visible;
                });
            });
            return next;
        });
        return true;
    };

    const unhideLastTreeHiddenSet = () => {
        const stack = hiddenPartsFiloStackRef.current;
        if (!stack.length) return false;
        const refs = stack.pop();
        return applyVisibilityForProductRefs(refs, true);
    };

    const handlePartDoubleClickHide = (partMeta) => {
        const selectionMeta = partMeta || selectedPart;
        if (!selectionMeta || !sceneRef.current) return;

        sceneRef.current?.hideSelected?.();

        sceneRef.current?.zoomInStep?.();
        skipMatrixApplyOnNextDeselectRef.current = true;
        sceneRef.current?.clearSelection?.();
    };

    const handleNodeCheckboxChange = (nodeId, checked) => {
        if (!assemblyIndex) return;

        const { nodeById } = assemblyIndex;
        const rootNode = nodeById.get(nodeId);
        if (!rootNode) return;

        const newVis = {};
        const productRefsToToggle = new Set();

        const applyToSubtree = (node) => {
            if (!node) return;
            const id = node._id;
            newVis[id] = checked;

            if (node.productRef !== null && node.productRef !== undefined) {
                productRefsToToggle.add(String(node.productRef));
            }

            if (Array.isArray(node.children)) {
                node.children.forEach(applyToSubtree);
            }
        };

        applyToSubtree(rootNode);

        if (productRefsToToggle.size > 0) {
            sceneRef.current?.setAssemblyNodesVisibility?.(
                Array.from(productRefsToToggle),
                checked
            );
        } else {
            // Even if no explicit productRef was found, force a sync so parents update
            sceneRef.current?.setAssemblyNodesVisibility?.([], checked);
        }

        setNodeVisibility((prev) => ({
            ...prev,
            ...newVis,
        }));
    };

    const toggleNodeExpanded = (nodeId) => {
        setExpanded((prev) =>
            prev.includes(nodeId)
                ? prev.filter((id) => id !== nodeId)
                : [...prev, nodeId]
        );
    };

    const handleNodeClick = (nodeId) => {
        setModelVisible(true);
        setNon3DMessageVisible(false);
        if (!assemblyIndex) return;
        const node = assemblyIndex.nodeById.get(nodeId);
        if (!node || !sceneRef.current) return;

        // Tree selection is a new logical selection. Clear the previous 3D
        // highlight/menu state first so a non-3D node cannot leave stale state
        // behind when the next selectable node is clicked.
        sceneRef.current?.clearSelection?.();
        clearSelectionActionToggles();
        setSelectedPart(null);
        setSelectedParts([]);
        setShowPartMenu(false);
        setShowMultiPartMenu(false);

        const {
            targetNode,
            refs,
            primaryRef,
            partMenuData,
        } = buildSelectionDescriptorFromNode(node);

        // Keep the exact tree/search row highlighted and scrolled into view;
        // targetNode is only the nearest orderable node used for 3D selection.
        setSelectedNodeId(nodeId);
        if (!refs.length) return;

        setPartMenuData({
            ...partMenuData,
            part_number:
                partMenuData.part_number ||
                (targetNode?.productRef != null
                    ? String(targetNode.productRef)
                    : (targetNode?.name || null)),
        });
        setShowPartMenu(true);
        setShowMultiPartMenu(false);
        setSelectedPart({
            name: targetNode?.name || node?.name || "Selected Part",
            uuid: `tree-${nodeId}`,
            type: "AssemblyNode",
            userData: {},
            material: null,
            object3D: null,
            assemblyNode: targetNode || node,
        });
        setSelectedParts([]);

        sceneRef.current?.selectAssemblySubtreeByProductRefs?.(refs, {
            primaryProductRef: primaryRef || refs[0],
            selectionNode: targetNode || node,
            // Keep the complete assembly in view; selection highlighting
            // identifies the part without moving the camera off the model.
            focus: false,
            emitSelectionCallbacks: false,
            includeAllOccurrences: false,
        });
    };

    const onTreeNodeClick = (nodeId) => {
        handleNodeClick(nodeId);
    };

    const filteredTree = useMemo(() => {
        if (!assemblyTree) return null;
        if (!treeSearchTerm || !treeSearchMatches.length) return assemblyTree;

        const matchingIds = new Set(treeSearchMatches);
        const keepMatches = (node) => {
            if (!node) return null;
            const children = Array.isArray(node.children)
                ? node.children.map(keepMatches).filter(Boolean)
                : [];
            if (matchingIds.has(node._id) || children.length > 0) {
                return { ...node, children };
            }
            return null;
        };

        return keepMatches(assemblyTree) || null;
    }, [assemblyTree, treeSearchTerm, treeSearchMatches]);

    const renderTreeItems = (node, level = 0) => {
        if (!node) return null;

        const nodeId = node._id || 'root';
        const { partName, partNumber, description } = resolveTreeNodeMeta(node);
        const checked = nodeVisibility[nodeId] ?? true;

        const hasChildren =
            Array.isArray(node.children) && node.children.length > 0;
        const isExpanded = expanded.includes(nodeId);
        const isSelected = selectedNodeId === nodeId;

        const isSearchMatch =
            !!treeSearchTerm && treeSearchMatches.includes(nodeId);
        const isActiveSearchMatch =
            isSearchMatch &&
            treeSearchMatches[treeSearchIndex] === nodeId;

        return (
            <Box key={nodeId}>
                <Box
                    ref={(el) => {
                        if (el) {
                            treeItemRefs.current.set(nodeId, el);
                        } else {
                            treeItemRefs.current.delete(nodeId);
                        }
                    }}
                    sx={(theme) => {
                        const base = {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            pl: 1 + level * 2.0,
                            pr: 0.5,
                            py: 0.25,
                            borderRadius: 0.75,
                            cursor: 'pointer',
                            minHeight: 44,
                        };

                        let bgcolor = 'transparent';
                        let color = theme.palette.text.primary;

                        const primaryLight = theme.palette.primary.light;
                        const warningLight = theme.palette.warning.light;

                        if (isSelected) {
                            // MAIN SELECTION HIGHLIGHT (PRIMARY)
                            bgcolor = alpha(primaryLight, 0.2);
                            color = theme.palette.primary.main;
                        } else if (isActiveSearchMatch) {
                            // current “jumped to” SEARCH MATCH (WARNING)
                            bgcolor = alpha(warningLight, 0.18);
                        } else if (isSearchMatch) {
                            // other SEARCH MATCHES (WARNING, softer)
                            bgcolor = alpha(warningLight, 0.08);
                        }

                        const hoverBg = (() => {
                            if (isSelected) {
                                return alpha(primaryLight, 0.25);
                            }
                            if (isActiveSearchMatch || isSearchMatch) {
                                return alpha(warningLight, 0.16);
                            }
                            // generic hover if not selected / match
                            return alpha(theme.palette.action.hover, 0.1);
                        })();

                        return {
                            ...base,
                            bgcolor,
                            color,
                            '&:hover': {
                                bgcolor: hoverBg,
                            },
                        };
                    }}
                    onClick={() => onTreeNodeClick(nodeId)}
                >
                    {hasChildren ? (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleNodeExpanded(nodeId);
                            }}
                            sx={{ mr: 0.25 }}
                        >
                            {isExpanded ? (
                                <ExpandMoreIcon fontSize="inherit" />
                            ) : (
                                <ChevronRightIcon fontSize="inherit" />
                            )}
                        </IconButton>
                    ) : (
                        <Box sx={{ width: 28, flexShrink: 0 }} />
                    )}

                    <Checkbox
                        size="small"
                        checked={checked}
                        onChange={(e) => {
                            e.stopPropagation();
                            handleNodeCheckboxChange(
                                nodeId,
                                e.target.checked
                            );
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        <Typography
                            variant="body1"
                            noWrap
                            sx={{
                                fontWeight: isSelected ? 600 : 400,
                            }}
                        >
                            {partName}
                        </Typography>
                        {(partNumber || description) && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                sx={{ fontSize: 11 }}
                            >
                                {partNumber ? `Part #: ${partNumber}` : ''}
                                {description ? ` / ${description}` : ''}
                            </Typography>
                        )}
                        <ReplacementPartNotice product={node} />
                    </Box>
                </Box>

                {hasChildren && isExpanded && (
                    <Box>
                        {node.children.map((child) =>
                            renderTreeItems(child, level + 1)
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    // ------- subassemblies visibility helper --------

    const applySubAssemblyVisibility = (selectedIds) => {
        if (!assemblyIndex || !sceneRef.current) return;
        const { nodeById, rootId } = assemblyIndex;
        const rootNode = nodeById.get(rootId);
        if (!rootNode || !Array.isArray(rootNode.children)) return;

        const visibilityPatch = {};
        const refsToShow = new Set();
        const refsToHide = new Set();

        const applyToSubtree = (node, visible) => {
            if (!node) return;
            visibilityPatch[node._id] = visible;

            const targetSet = visible ? refsToShow : refsToHide;
            collectProductRefsFromNode(node, targetSet);

            if (Array.isArray(node.children)) {
                node.children.forEach((child) =>
                    applyToSubtree(child, visible)
                );
            }
        };

        const showAll = !selectedIds || selectedIds.length === 0;

        rootNode.children.forEach((child) => {
            const isSelected = showAll
                ? true
                : selectedIds.includes(child._id);
            applyToSubtree(child, isSelected);
        });

        if (refsToShow.size > 0) {
            sceneRef.current?.setAssemblyNodesVisibility?.(
                Array.from(refsToShow),
                true
            );
        }

        if (refsToHide.size > 0) {
            sceneRef.current?.setAssemblyNodesVisibility?.(
                Array.from(refsToHide),
                false
            );
        }

        if (refsToShow.size === 0 && refsToHide.size === 0) {
            sceneRef.current?.setAssemblyNodesVisibility?.([], true);
        }

        setNodeVisibility((prev) => ({
            ...prev,
            ...visibilityPatch,
        }));
    };

    const handleSubAssemblyCardClick = (event, nodeId, nodeData) => {
        if (!assemblyIndex || !sceneRef.current) return;
        setModelVisible(true);
        setNon3DMessageVisible(false);
        const isModifier = event.ctrlKey || event.metaKey;
        setSelectVisibleActive(false);
        let newSelectedIds;
        if (isModifier) {
            const current = selectedSubAssemblyIds;
            if (current.includes(nodeId)) {
                newSelectedIds = current.filter((id) => id !== nodeId);
            } else {
                newSelectedIds = [...current, nodeId];
            }
        } else {
            setSelectedPart(null);
            clearSelectionActionToggles();
            setSelectedParts(null);
            newSelectedIds = [nodeId];
        }

        setSelectedSubAssemblyIds(newSelectedIds);
        applySubAssemblyVisibility(newSelectedIds);

        if (!isModifier && newSelectedIds.length === 1) {
            const selectedNode = nodeData || assemblyIndex.nodeById.get(nodeId);
            if (!selectedNode) return;

            const matched = findMatchingProductForPart(
                selectedNode?.name,
                selectedNode?.productRef
            );
            if (matched) {
                setPartMenuData({
                    part_name: matched.product_name || selectedNode?.name || "Selected Subassembly",
                    part_number: matched.product_number || selectedNode?.name || null,
                    part_type: matched.ProductType || matched.ProductTypeKey || "N/A",
                    product_id: matched.product_id || null,
                    price: getSitePrice(matched),
                    purchasable: matched.purchasable,
                    isOrderable: true,
                    availableIn3D: true,
                });
            } else {
                const fallbackPart = selectedNode?.productRef != null
                    ? String(selectedNode.productRef)
                    : (selectedNode?.name || null);
                setPartMenuData({
                    part_name: selectedNode?.name || "Selected Subassembly",
                    part_number: fallbackPart,
                    part_type: null,
                    product_id: null,
                    price: null,
                    purchasable: 0,
                    isOrderable: false,
                    availableIn3D: true,
                });
            }
            setShowPartMenu(true);
            setShowMultiPartMenu(false);
            setSelectedNodeId(nodeId);
            setSelectedPart({
                name: selectedNode?.name || "Selected Subassembly",
                uuid: `subassembly-${nodeId}`,
                type: "SubAssembly",
                userData: {},
                material: null,
                object3D: null,
                assemblyNode: selectedNode,
            });
            setSelectedParts([]);

            const refsToShow = new Set();
            collectProductRefsFromNode(selectedNode, refsToShow);
            const refs = Array.from(refsToShow);
            const selectedNodeRef =
                selectedNode?.productRef !== undefined &&
                    selectedNode?.productRef !== null
                    ? String(selectedNode.productRef)
                    : null;
            const preferredRef =
                selectedNodeRef && refs.includes(selectedNodeRef)
                    ? selectedNodeRef
                    : null;

            if (refs.length > 0) {
                sceneRef.current?.clearGlobalTransparency?.();
                sceneRef.current?.showOnlyProductRefs?.(refs, {
                    emptyMode: "hideAll",
                    focus: false,
                });
                sceneRef.current?.selectAssemblySubtreeByProductRefs?.(refs, {
                    primaryProductRef: preferredRef || refs[0],
                    focus: false,
                    emitSelectionCallbacks: false,
                });
                setIsolateSelectionToggle(true);
            }
        }
    };

    // ------- context menu: unhide last hidden --------
    const handleCanvasContextMenu = (e) => {
        const target = e.target;
        if (!(target instanceof HTMLElement)) return;

        const isCanvas = !!target.closest('canvas');
        if (!isCanvas) return;

        e.preventDefault();
        const didUnhide = sceneRef.current?.unhideLastHidden?.();
        if (!didUnhide) {
            unhideLastTreeHiddenSet();
        }
    };

    // ---------- top / side UI handlers ----------

    const handleTabChange = (_event, newValue) => {
        setSuspendRsplFilter(false);
        setTabValue(newValue);
        if (newValue === 1) {
            // show loader immediately
            setRsplLoading(true);
            setRsplReady(false);
            setModelVisible(true);
            setNon3DMessageVisible(false);

            // defer RSPL heavy rendering so UI paints instantly
            setTimeout(() => {
                setRsplReady(true);
                setRsplLoading(false);
            }, 100);
        }
    };

    const [moreAnchorEl, setMoreAnchorEl] = useState(null);
    const isMoreMenuOpen = Boolean(moreAnchorEl);

    const handleMoreClick = (event) => {
        setMoreAnchorEl(event.currentTarget);
    };

    const handleMoreClose = () => {
        setMoreAnchorEl(null);
    };

    const handleSettings = () => {
        setSettingsOpen(true);
        handleMoreClose();
    };

    const handleShadedView = () => {
        // TODO: wire to material change if desired
        handleMoreClose();
    };

    const handleShadedWireView = () => {
        // TODO: wire to material change if desired
        handleMoreClose();
    };

    const handleMachineTransparencyToggle = () => {
        skipMatrixApplyOnNextDeselectRef.current = false;
        setMachineTransparentToggle((prev) => {
            const next = !prev;

            // Turning machine transparency OFF should clear any solid exceptions
            // that were created only to punch holes through the transparent machine.
            if (!next) {
                setPartTransparencyOverrides((currentOverrides) =>
                    Object.fromEntries(
                        Object.entries(currentOverrides).filter(([, isTransparent]) => isTransparent)
                    )
                );
            }

            return next;
        });
    };

    const handlePartTransparencyToggle = () => {
        if (!selectedPart) return;
        skipMatrixApplyOnNextDeselectRef.current = false;
        const key = getTransparencySelectionKey(selectedPart);
        if (!key) return;
        const current = getEffectivePartTransparency(selectedPart);
        const next = !current;

        if (!next && !machineTransparentToggle) {
            sceneRef.current?.setSelectionKeysSolid?.([key]);
        }

        setPartTransparencyOverrides((prev) => {
            const base = machineTransparentToggle;
            const updated = { ...prev };

            if (next === base) {
                delete updated[key];
            } else {
                updated[key] = next;
            }
            return updated;
        });
    };

    const handleIsolateToggle = () => {
        if (!selectedPart) return;
        setIsolateSelectionToggle((prev) => {
            const next = !prev;
            if (!sceneRef.current) return next;

            if (next) {
                const currentSelection = Array.isArray(selectedParts)
                    ? selectedParts.filter(Boolean)
                    : [];
                const isMultiSelection = currentSelection.length > 1;
                const selectedRef =
                    selectedPart?.assemblyNode?.productRef !== undefined &&
                        selectedPart?.assemblyNode?.productRef !== null
                        ? String(selectedPart.assemblyNode.productRef)
                        : null;

                // Ensure ModelScene has a valid current selection root for synthetic
                // RSPL/Subassembly selections before isolate is applied.
                if (!isMultiSelection && selectedRef) {
                    sceneRef.current?.selectAssemblySubtreeByProductRefs?.([selectedRef], {
                        primaryProductRef: selectedRef,
                        focus: false,
                        emitSelectionCallbacks: false,
                        includeAllOccurrences: false,
                    });
                }

                sceneRef.current?.isolateSelected?.({ focus: false });
                return next;
            }

            // Toggle OFF isolate: restore the non-isolated matrix view immediately.
            const shouldRespectRsplFilter =
                !suspendRsplFilter &&
                !machineTransparentToggle && tabValue === 1 && !!rsplReady;
            const filterRefs = shouldRespectRsplFilter ? currentRsplProductRefs : [];

            sceneRef.current?.showOnlyProductRefs?.(filterRefs, {
                emptyMode: shouldRespectRsplFilter ? "hideAll" : "showAll",
                focus: false,
            });

            if (machineTransparentToggle) {
                sceneRef.current?.showOnlyProductRefs?.([], { focus: false });
                sceneRef.current?.showMachineTransparentExceptSelected?.({ opacity: 0.15 });
            }

            return next;
        });
    };

    useEffect(() => {
        if (!sceneRef.current) return;
        if (skipMatrixApplyOnNextDeselectRef.current && !selectedPart) {
            skipMatrixApplyOnNextDeselectRef.current = false;
            return;
        }

        const shouldRespectRsplFilter =
            !suspendRsplFilter &&
            !machineTransparentToggle && tabValue === 1 && !!rsplReady;
        const filterRefs = shouldRespectRsplFilter ? currentRsplProductRefs : [];
        const baseTransparent = machineTransparentToggle && !isolateSelectionToggle;
        const overrideEntries = Object.entries(partTransparencyOverrides);
        const solidOverrideKeys = overrideEntries
            .filter(([, isTransparent]) => !isTransparent && baseTransparent)
            .map(([key]) => key);
        const transparentOverrideKeys = overrideEntries
            .filter(([, isTransparent]) => isTransparent && !baseTransparent)
            .map(([key]) => key);

        if (!baseTransparent) {
            sceneRef.current?.clearGlobalTransparency?.();
        }

        if (isolateSelectionToggle) {
            if (transparentOverrideKeys.length > 0) {
                sceneRef.current?.setSelectionKeysTransparent?.(transparentOverrideKeys, { opacity: 0.2 });
            }

            if (solidOverrideKeys.length > 0) {
                sceneRef.current?.setSelectionKeysSolid?.(solidOverrideKeys);
            }

            return;
        }

        sceneRef.current?.showOnlyProductRefs?.(filterRefs, {
            emptyMode: shouldRespectRsplFilter ? "hideAll" : "showAll",
            focus: false,
        });

        if (baseTransparent) {
            sceneRef.current?.showOnlyProductRefs?.([], { focus: false });
            sceneRef.current?.showMachineTransparentExceptSelectionKeys?.(solidOverrideKeys, { opacity: 0.15 });
        }

        if (transparentOverrideKeys.length > 0) {
            sceneRef.current?.setSelectionKeysTransparent?.(transparentOverrideKeys, { opacity: 0.2 });
        }

        if (solidOverrideKeys.length > 0) {
            sceneRef.current?.setSelectionKeysSolid?.(solidOverrideKeys);
        }
    }, [
        selectedPart,
        machineTransparentToggle,
        partTransparencyToggle,
        partTransparencyOverrides,
        isolateSelectionToggle,
        tabValue,
        rsplReady,
        currentRsplProductRefs,
        suspendRsplFilter,
    ]);

    useEffect(() => {
        if (!modelLoaded) return;
        skipMatrixApplyOnNextDeselectRef.current = false;
        hiddenPartsFiloStackRef.current = [];
        sceneRef.current?.resetModel?.();
        sceneRef.current?.clearSelection?.();
        sceneRef.current?.clearGlobalTransparency?.();
        sceneRef.current?.showOnlyProductRefs?.([]);
        setSelectedPart(null);
        setSelectedParts(null);
        setPartTransparencyOverrides({});
        setShowPartMenu(false);
        setShowMultiPartMenu(false);
        setMachineTransparentToggle(false);
        setPartTransparencyToggle(false);
        setIsolateSelectionToggle(false);
        setOccurrenceToggle(false);
        setSelectVisibleActive(false);
        setSuspendRsplFilter(false);
    }, [modelLoaded]);

    const handleBom = () => {
        const bomRows = buildBomRows();
        setBomData(bomRows);
        setBomOpen(true);
        handleMoreClose();
    };

    const handleSettingsClose = () => {
        setSettingsOpen(false);
    };

    const handleBomClose = () => {
        setBomOpen(false);
    };

    const handleCartClick = () => {
        setCartOpen((prev) => !prev);
    };

    const resetModel = () => {
        // Reset 3D model & remove highlights
        skipMatrixApplyOnNextDeselectRef.current = false;
        hiddenPartsFiloStackRef.current = [];
        setSuspendRsplFilter(tabValue === 1);
        sceneRef.current?.resetModel?.();
        sceneRef.current?.clearSelection?.();
        sceneRef.current?.clearGlobalTransparency?.();
        sceneRef.current?.showOnlyProductRefs?.([]);
        setModelVisible(true);
        setNon3DMessageVisible(false);
        setNon3DPartNumber("");
        keepPartMenuOnNextClearRef.current = false;

        setSelectVisibleActive(false);
        // Close Part Menu
        setShowPartMenu(false);
        setPartMenuData(null);

        // Close Multi-Part menu
        setShowMultiPartMenu(false);
        setMultiPartSelection([]);
        setPartMenuDelta({ x: 0, y: 0 });
        setMultiPartMenuDelta({ x: 0, y: 0 });

        // Clear selected part & selection arrays
        setSelectedPart(null);
        setSelectedParts([]);

        // Reset toggles
        setOccurrenceToggle(false);
        setMachineTransparentToggle(false);
        setPartTransparencyToggle(false);
        setIsolateSelectionToggle(false);
        setActiveRsplPartNumber(null);

        // Reset tree visibility & expansions
        if (assemblyIndex) {
            const { nodeById, rootId } = assemblyIndex;

            const vis = {};
            nodeById.forEach((_node, id) => {
                vis[id] = true;
            });
            setNodeVisibility(vis);
            setExpanded([rootId]);
        }

        // Reset sub-assemblies
        setSelectedSubAssemblyIds([]);
        setSubSearchTerm('');

        // Reset tree search
        setTreeSearchTerm('');
        setTreeSearchMatches([]);
        setTreeSearchIndex(0);

        // Reset RSPL search state
        setRsplSearchTerm("");
        setRsplSearchResults([]);
        setRsplSearchIndex(0);

        // Reset explode
        setExplodeEnabled(false);
        setExplodeValue(0.5);
        setSettings((prev) => ({
            ...prev,
            explodeEnabled: false,
            explodeValue: 0.5,
        }));
    };

    const buildBomRows = () => {
        if (!assemblyTree) return [];

        const quantityMap = new Map();

        const traverse = (node) => {
            if (!node) return;
            const partName = node.name?.trim();

            if (partName) {
                if (!quantityMap.has(partName)) {
                    quantityMap.set(partName, 0);
                }
                quantityMap.set(partName, quantityMap.get(partName) + 1);
            }

            if (Array.isArray(node.children)) {
                node.children.forEach(traverse);
            }
        };

        traverse(assemblyTree);

        const rows = [];

        quantityMap.forEach((qty, name) => {
            const product = findMatchingProductForPart(name);

            rows.push({
                id: name,
                part_name: product?.product_name || "",
                part_number: product?.product_number || name,
                quantity: qty,
                price:
                    product && product.purchasable
                        ? getSitePrice(product)
                        : "",
                alternative_item_number: product?.alternative_item_number,
                replacement: product?.replacement,
            });
        });

        return rows;
    };

    const bomColumns = [
        { field: "part_name", headerName: t('TABLE_HEADERS.PART_NAME'), flex: 1 },
        { field: "part_number", headerName: t('TITLES.PART_NUMBER_1'), flex: 1 },
        { field: "quantity", headerName: t('TABLE_HEADERS.QUANTITY'), width: 150 },
        { field: "price", headerName: t('TABLE_HEADERS.PRICE'), width: 150 },
    ];



    const showParentFromSelection = () => {
        // New behavior: hide everything except current selection and
        // progressively reveal parents via ModelScene's stepping logic
        sceneRef.current?.stepShowParentForSelection?.();
    };

    const rsplRenderedByType = useMemo(() => {
        const map = {};

        // Per-type lists
        productTypes.forEach((type) => {
            const key = normalizeRsplType(type);
            const items = productsByType[key] ?? [];

            map[type] = items
                .map((prod) => {
                    const in3D = !!product3DMap[prod.product_number];
                    const isWarning = rsplSearchResults.some(
                        (r) => r.product_number === prod.product_number
                    );

                    return (
                        <Box
                            id={`rspl-${prod.product_number}`}
                            key={prod.product_number}
                            sx={{
                                cursor: "pointer",
                                borderRadius: "6px",
                            }}
                            onClick={() => handleRSPLPartClick(prod)}
                        >
                            <MemoPartCard
                                id={`rspl-${prod.product_number}`}
                                showWarning
                                showPrice={canShowPrice}
                                isActive={activeRsplPartNumber === prod.product_number}
                                warning={isWarning}
                                data={{
                                    part_name: prod.product_name,
                                    part_number: prod.product_number,
                                    purchasable: prod.purchasable,
                                    price: getSitePrice(prod),
                                    availableIn3D: in3D,
                                    productImageURL: prod.productImageURL,
                                }}
                            />
                        </Box>
                    );
                });
        });

        // "All" = union of all productTypes (no "Other" since productTypes already excludes it)
        const allItemsMap = new Map(); // dedupe by product_number

        productTypes.forEach((type) => {
            const key = normalizeRsplType(type);
            const items = productsByType[key] ?? [];
            items
                .forEach((prod) => {
                    if (!allItemsMap.has(prod.product_number)) {
                        allItemsMap.set(prod.product_number, prod);
                    }
                });
        });

        map["All"] = Array.from(allItemsMap.values()).map((prod) => {
            const in3D = !!product3DMap[prod.product_number];
            const isWarning = rsplSearchResults.some(
                (r) => r.product_number === prod.product_number
            );

            return (
                <Box
                    id={`rspl-${prod.product_number}`}
                    key={prod.product_number}
                    sx={{
                        cursor: "pointer",
                        borderRadius: "6px",
                    }}
                    onClick={() => handleRSPLPartClick(prod)}
                >
                    <MemoPartCard
                        id={`rspl-${prod.product_number}`}
                        showWarning
                        showPrice={canShowPrice}
                        isActive={activeRsplPartNumber === prod.product_number}
                        warning={isWarning}
                        data={{
                            part_name: prod.product_name,
                            part_number: prod.product_number,
                            purchasable: prod.purchasable,
                            price: getSitePrice(prod),
                            availableIn3D: in3D,
                            productImageURL: prod.productImageURL,
                        }}
                    />
                </Box>
            );
        });

        return map;
    }, [
        products,
        productTypes,
        productsByType,
        product3DMap,
        activeRsplPartNumber,
        rsplSearchResults,
        handleRSPLPartClick,
    ]);


    const handleCloseModel = () => {
        Swal.fire({
            title: t('UI_TEXT.ARE_YOU_SURE_YOU_WANT_TO_CLOSE'),
            text: canShowAddToCart
                ? t('UI_TEXT.ANY_ITEMS_ADDED_IN_THE_MODEL_CART_WILL_NOT_BE_ADDED_TO_THE_APPLICATION_CART')
                : '',
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: t('BUTTON.YES_CLOSE'),
            cancelButtonText: t('BUTTON.CANCEL'),

        }).then((result) => {
            if (result.isConfirmed) {
                navigate(returnPath);
            }
        });
    };

    const handleHardRefresh = () => {
        const url = new URL(window.location.href);
        url.searchParams.set('_hard_refresh', `${Date.now()}`);
        window.location.replace(url.toString());
    };

    const handleContactCcrClick = () => {
        setHelpOpen(false);
        setCcrOpen(true);
    };

    const helpSections = [
        {
            title: "Hamburger Menu",
            items: [
                {
                    icon: <MenuIcon fontSize="small" />,
                    title: "Menu Panel",
                    description:
                        "Open or close the left panel.\nSwitch between Sub Assemblies, RSPL Parts, and BOM.",
                },
            ],
        },
        {
            title: "Part Menu",
            items: [
                {
                    icon: <Launch fontSize="small" />,
                    title: "Open Part",
                    description:
                        "Open the selected part's product page.\nAvailable when a link is provided.",
                },
                {
                    icon: <Close fontSize="small" />,
                    title: "Close Menu",
                    description:
                        "Dismiss the part menu popover.\nSelection stays active in the model.",
                },
                {
                    icon: <ShoppingCart fontSize="small" />,
                    title: t('BUTTONS.ADD_TO_CART'),
                    description:
                        "Add the selected part to the model cart.\nAdjust quantities in the cart drawer.",
                },
                {
                    icon: <Summarize fontSize="small" />,
                    title: "Add to Request",
                    description:
                        "Send non-purchasable parts to the request list.\nSubmit with other parts later.",
                },
                {
                    icon: <KeyboardDoubleArrowUp fontSize="small" />,
                    title: "Show Parent",
                    description:
                        "Reveal the selected part's parent context.\nHelps you see where it lives.",
                },
                {
                    icon: <NorthEast fontSize="small" />,
                    title: "Isolate",
                    description:
                        "Hide everything except the selected part.\nGreat for focused inspection.",
                },
                {
                    icon: (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                            <LibraryAddCheck fontSize="small" />
                            <CheckBoxIcon fontSize="small" />
                        </Box>
                    ),
                    title: "Select Occurrences",
                    description:
                        "Toggle between one instance and all occurrences.\nUseful for repeated parts.",
                },
            ],
        },
        {
            title: "Bottom Toolbar",
            items: [
                {
                    control: (
                        <Button variant="modelMenuButtons">
                            <HomeIcon />
                        </Button>
                    ),
                    title: "Home",
                    description:
                        "Reset the model view and selections.\nRestores default visibility.",
                },

                {
                    control: (
                        <Button
                            variant="modelMenuButtons"
                            sx={(theme) =>
                                explodeEnabled
                                    ? {
                                        backgroundColor: theme.palette.primary.main,
                                        color: '#fff',
                                    }
                                    : {}
                            }
                        >
                            <OpenInFull />
                        </Button>
                    ),
                    title: "Explode Model",
                    description:
                        "Spread parts apart for clarity.\nUse the explode slider when enabled.",
                },
                {
                    control: (
                        <Button variant="modelMenuButtons" disabled={!selectedPart}>
                            <ZoomInIcon />
                        </Button>
                    ),
                    title: "Zoom to Selected",
                    description:
                        "Frame the selected part in view.\nDisabled until a part is selected.",
                },

                {
                    control: (
                        <Button
                            variant="modelMenuButtons"
                            sx={(theme) =>
                                selectVisibleActive
                                    ? {
                                        backgroundColor: theme.palette.primary.main,
                                        color: "#fff",
                                    }
                                    : {}
                            }
                        >
                            <CheckBoxIcon />
                        </Button>
                    ),
                    title: "Select Visible",
                    description:
                        "Select all currently visible parts.\nClick again to clear.",
                },
                {
                    control: (
                        <Button variant="modelMenuButtons" disabled={!selectedPart}>
                            <KeyboardDoubleArrowUp />
                        </Button>
                    ),
                    title: "Parent",
                    description:
                        "Reveal parent assemblies in the current view.\nDisabled until a part is selected.",
                },
                {
                    control: (
                        <Button variant="modelMenuButtons">
                            <QuestionMark />
                        </Button>
                    ),
                    title: "Help",
                    description:
                        "Open this guide.\nQuick reference for controls.",
                },
                {
                    control: (
                        <Button variant="modelMenuButtons">
                            <MoreVertIcon />
                        </Button>
                    ),
                    title: "More",
                    description:
                        "Open additional viewer actions.\nBOM and extra options live here.",
                },
                {
                    control: (
                        <Button
                            variant="modelMenuButtons"
                            disabled={!selectedPart}
                            endIcon={<NorthEast fontSize="small" />}
                            sx={(theme) => ({
                                width: 'auto',
                                minWidth: 'fit-content',
                                px: 1.5,
                                borderRadius: '15px',
                                textTransform: "none",
                                whiteSpace: 'nowrap',
                                overflow: 'visible',
                                ...(isolateSelectionToggle
                                    ? {
                                        backgroundColor: theme.palette.primary.main,
                                        color: "#fff",
                                    }
                                    : {}),
                            })}
                        >
                            Isolate
                        </Button>
                    ),
                    title: "Isolate",
                    description:
                        "Show only selected part(s).\nDisabled until a part is selected.",
                },
                {
                    control: (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Button variant="modelMenuButtons">
                                <OpenWithIcon />
                            </Button>
                            <Typography variant="caption" color="text.secondary">/</Typography>
                            <Button
                                variant="modelMenuButtons"
                                sx={(theme) => ({
                                    backgroundColor: theme.palette.primary.main,
                                    color: "#fff",
                                })}
                            >
                                <Loop />
                            </Button>
                        </Box>
                    ),
                    title: "Rotate / Pan Toggle",
                    description:
                        "Switch drag behavior between rotate and pan.\nActive state means Pan mode.",
                },

                {
                    control: (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Button
                                variant="modelMenuButtons"
                                sx={{
                                    width: 'auto',
                                    minWidth: 'fit-content',
                                    px: 1.5,
                                    borderRadius: '15px',
                                    textTransform: "none",
                                    whiteSpace: 'nowrap',
                                    overflow: 'visible',
                                    gap: 0.5,
                                }}
                            >
                                Part
                                <PiDropSimpleFill size={14} />
                            </Button>
                            <Typography variant="caption" color="text.secondary">/</Typography>
                            <Button
                                variant="modelMenuButtons"
                                sx={(theme) => ({
                                    width: 'auto',
                                    minWidth: 'fit-content',
                                    px: 1.5,
                                    borderRadius: '15px',
                                    textTransform: "none",
                                    whiteSpace: 'nowrap',
                                    overflow: 'visible',
                                    gap: 0.5,
                                    backgroundColor: theme.palette.primary.main,
                                    color: "#fff",
                                })}
                            >
                                Part
                                <BsTransparency size={14} />
                            </Button>
                        </Box>
                    ),
                    title: "Part Transparent / Solid",
                    description:
                        "Toggle selected-part transparency.\nDisabled until a part is selected.",
                },
                {
                    control: (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Button
                                variant="modelMenuButtons"
                                sx={{
                                    width: 'auto',
                                    minWidth: 'fit-content',
                                    px: 1.5,
                                    borderRadius: '15px',
                                    textTransform: "none",
                                    whiteSpace: 'nowrap',
                                    overflow: 'visible',
                                    gap: 0.5,
                                }}
                            >
                                Machine
                                <PiDropSimpleFill size={14} />
                            </Button>
                            <Typography variant="caption" color="text.secondary">/</Typography>
                            <Button
                                variant="modelMenuButtons"
                                sx={(theme) => ({
                                    width: 'auto',
                                    minWidth: 'fit-content',
                                    px: 1.5,
                                    borderRadius: '15px',
                                    textTransform: "none",
                                    whiteSpace: 'nowrap',
                                    overflow: 'visible',
                                    gap: 0.5,
                                    backgroundColor: theme.palette.primary.main,
                                    color: "#fff",
                                })}
                            >
                                Machine
                                <BsTransparency size={14} />
                            </Button>
                        </Box>
                    ),
                    title: "Machine Transparent / Solid",
                    description:
                        "Toggle full-machine transparency.\nWhen ON, machine becomes transparent and selected part behavior follows toolbar matrix rules.",
                },
            ],
        },
    ];

    const helpContent = (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                p: { xs: 0.5, md: 1 },
            }}
        >
            {helpSections.map((section) => {
                const isBottomToolbarSection = section.title === "Bottom Toolbar";
                return (
                    <Box
                        key={section.title}
                        sx={{
                            borderRadius: 2,
                            p: 1.5,
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.25 }}>
                            {section.title}
                        </Typography>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: isBottomToolbarSection
                                    ? "1fr"
                                    : { xs: "1fr", md: "1fr 1fr" },
                                gap: 1,
                            }}
                        >
                            {section.items.map((item) => (
                                <Box
                                    key={item.title}
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "max-content minmax(0,1fr)" },
                                        columnGap: 1.25,
                                        rowGap: 0.5,
                                        alignItems: "start",
                                        p: 1.25,

                                    }}
                                >
                                    <Box
                                        sx={{
                                            flexShrink: 0,
                                            pointerEvents: "none",
                                            width: "max-content",
                                            display: "flex",
                                            justifyContent: "flex-start",
                                            mt: { xs: 0, sm: 0.25 },
                                        }}
                                    >
                                        {item.control ? (
                                            item.control
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: "50%",
                                                    backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.06),
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                {item.icon}
                                            </Box>
                                        )}
                                    </Box>

                                    <Box sx={{ minWidth: 0, alignSelf: "center" }}>
                                        <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5, lineHeight: 1.2 }}>
                                            {item.title}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ whiteSpace: "pre-line", lineHeight: 1.55, display: "block" }}
                                        >
                                            {item.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );



    // ---------- layout ----------

    const containerStyles = {
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        background: (theme) => theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
    };

    // "Select visible" handler
    const handleSelectVisibleParts = () => {
        // If already active → clicking again should disable & clear everything
        if (selectVisibleActive) {
            // Deactivate toggle
            setSelectVisibleActive(false);

            // Clear selections in the 3D scene
            sceneRef.current?.clearSelection?.();
            sceneRef.current?.resetModel?.();

            // Close UI menus
            setShowPartMenu(false);
            setShowMultiPartMenu(false);
            setMultiPartSelection([]);
            setSelectedPart(null);
            clearSelectionActionToggles();
            setSelectedParts([]);

            return;
        }

        // -------- ACTIVATING SELECT VISIBLE --------
        const result = sceneRef.current?.selectVisibleParts?.() || [];

        // Hide any single-part menu
        setShowPartMenu(false);

        if (Array.isArray(result) && result.length > 0) {
            const normalizeKey = (value) =>
                String(value ?? '')
                    .trim()
                    .toLowerCase();

            const dedupSelection = new Map();
            result.forEach((part, idx) => {
                const key =
                    part?.assemblyNode?.productRef !== undefined &&
                        part?.assemblyNode?.productRef !== null
                        ? `p:${part.assemblyNode.productRef}`
                        : `u:${part?.uuid ?? idx}`;
                if (!dedupSelection.has(key)) {
                    dedupSelection.set(key, part);
                }
            });

            if (assemblyIndex && level1SubAssemblies.length > 0) {
                const subAssemblyIdByKey = new Map();
                level1SubAssemblies.forEach(({ id, node }) => {
                    const keys = [
                        normalizeKey(node?.productRef),
                        normalizeKey(node?.name),
                    ].filter(Boolean);
                    keys.forEach((key) => {
                        if (!subAssemblyIdByKey.has(key)) {
                            subAssemblyIdByKey.set(key, new Set());
                        }
                        subAssemblyIdByKey.get(key).add(id);
                    });
                });

                const matchedSubAssemblyIds = new Set();
                result.forEach((part) => {
                    const candidateKeys = [
                        normalizeKey(part?.assemblyNode?.productRef),
                        normalizeKey(part?.assemblyNode?.name),
                        normalizeKey(part?.name),
                    ].filter(Boolean);

                    candidateKeys.forEach((key) => {
                        const matches = subAssemblyIdByKey.get(key);
                        if (matches) {
                            matches.forEach((id) => matchedSubAssemblyIds.add(id));
                        }
                    });
                });

                if (matchedSubAssemblyIds.size > 0) {
                    const refsToShow = new Set();

                    const collectSubtreeParts = (node) => {
                        if (!node) return;
                        if (node.productRef !== null && node.productRef !== undefined) {
                            const productRef = String(node.productRef);
                            refsToShow.add(productRef);
                            const key = `p:${productRef}`;
                            if (!dedupSelection.has(key)) {
                                dedupSelection.set(key, {
                                    name: node?.name || productRef,
                                    uuid: `subassembly-${node._id}`,
                                    type: 'AssemblyNode',
                                    assemblyNode: node,
                                });
                            }
                        }

                        if (Array.isArray(node.children)) {
                            node.children.forEach(collectSubtreeParts);
                        }
                    };

                    matchedSubAssemblyIds.forEach((subAssemblyId) => {
                        const subNode = assemblyIndex.nodeById.get(subAssemblyId);
                        collectSubtreeParts(subNode);
                    });

                    if (refsToShow.size > 0) {
                        sceneRef.current?.setAssemblyNodesVisibility?.(
                            Array.from(refsToShow),
                            true
                        );
                    }

                    setSelectedSubAssemblyIds((prev) =>
                        Array.from(new Set([...(prev || []), ...matchedSubAssemblyIds]))
                    );
                }
            }

            const finalSelection = Array.from(dedupSelection.values());
            setSelectedParts(finalSelection);
            setMultiPartSelection(finalSelection);
            setShowMultiPartMenu(true);
            setSelectVisibleActive(true);
        } else {
            setMultiPartSelection([]);
            setSelectedParts([]);
            setShowMultiPartMenu(false);
            setSelectVisibleActive(false);
        }
    };



    const handleCanvasPointerMissed = (event) => {
        if (typeof event?.delta === "number" && event.delta > 4) return;
        const clickDetail =
            event?.detail ??
            event?.nativeEvent?.detail ??
            event?.sourceEvent?.detail;
        // Ignore multi-click miss events that are part of double-click interactions.
        if (typeof clickDetail === "number" && clickDetail > 1) return;
        const hasActiveSelection =
            !!selectedPart || (Array.isArray(selectedParts) && selectedParts.length > 0);

        // Do not clear a selection when this miss belongs to the same pointer
        // interaction that just selected a mesh. This is especially important
        // on the initial RSPL view, where the menu otherwise flashes and closes.
        if (Date.now() - lastModelSelectionAtRef.current < 350) return;
        if (!hasActiveSelection) return;

        skipMatrixApplyOnNextDeselectRef.current = hasActiveSelection;
        sceneRef.current?.clearSelection?.();
    };
    const handleMultipleAddToCart = () => {
        if (!multiPartSelection || multiPartSelection.length === 0) {
            Swal.fire({
                icon: "info",
                title: "<div style='text-align:center;width:100%;'>No Parts Selected</div>",
                html: "<div style='text-align:center;width:100%;'>There are no visible parts selected.</div>",
            });
            return;
        }

        let purchasableParts = [];
        let requestListParts = [];
        let nonOrderableParts = [];

        // Categorize each part
        multiPartSelection.forEach((part) => {
            const label = part.assemblyNode?.name || part.name || "";
            const matchedProduct = findMatchingProductForPart(
                label,
                part?.assemblyNode?.productRef
            );

            if (!matchedProduct) {
                nonOrderableParts.push(label);
                return;
            }

            const effectiveProduct = getEffectiveProductDisplay(matchedProduct, {
                machineId: machine_id,
                currency: selectedSiteCurrency,
            });
            const partData = {
                product_id: effectiveProduct.product_id,
                part_number: effectiveProduct.product_number,
                part_name: effectiveProduct.product_name,
                price: effectiveProduct.price ?? getSitePrice(effectiveProduct),
                purchasable: effectiveProduct.purchasable,
            };

            if (effectiveProduct.purchasable) {
                if (canShowAddToCart) purchasableParts.push(partData);
            } else if (canShowAddToRequestList) {
                requestListParts.push(partData);
            }
        });

        // Add to cart
        if (canShowAddToCart) {
            setCartItems((prev) => {
                const updated = [...prev];
                purchasableParts.forEach((p) => {
                    if (!updated.find((i) => i.part_number === p.part_number)) {
                        updated.push({ ...p, qty: 1 });
                    }
                });
                return updated;
            });
        }

        // Add to request list
        if (canShowAddToRequestList) {
            setRequestItems((prev) => {
                const updated = [...prev];
                requestListParts.forEach((p) => {
                    if (!updated.find((i) => i.part_number === p.part_number)) {
                        updated.push({ part_number: p.part_number, product_id: p.product_id });
                    }
                });
                return updated;
            });
        }

        // ---------- Swal Logic (INLINE CENTERED) ---------- //
        const hasPurchasable = purchasableParts.length > 0;
        const hasRequestable = requestListParts.length > 0;
        const hasNonOrderable = nonOrderableParts.length > 0;

        let icon = "success";
        let title = "Parts Processed";
        let html = `
        <div style="text-align:center;width:100%;">
            <b>Purchasable (Added to Cart):</b> ${purchasableParts.length}<br/>
            <b>Request List Items:</b> ${requestListParts.length}<br/>
            ${hasNonOrderable ? `<b>Non-Orderable Parts:</b> ${nonOrderableParts.length}` : ""}
        </div>
    `;

        if (!hasPurchasable && !hasRequestable && hasNonOrderable) {
            icon = "warning";
            title = "No Purchasable or Requestable Parts";
            html = `
            <div style="text-align:center;width:100%;">
                None of the selected parts can be added.<br/>
                <b>Non-Orderable Parts:</b> ${nonOrderableParts.length}
            </div>
        `;
        }

        Swal.fire({
            icon,
            title: `<div style="text-align:center;width:100%;">${title}</div>`,
            html,
            confirmButtonText: t('GENERAL.OK'),
        });

        setShowMultiPartMenu(false);
    };

    const handleAddAllProductType = (type) => {
        if (!canShowAddToCart && !canShowAddToRequestList) return;
        const typeKey = normalizeRsplType(type);
        const parts = productsByType[typeKey];
        const mixedRsplMessage = "Some of the selected parts need up to date pricing, do you want to add them to customer care requests?";

        if (!parts || parts.length === 0) {
            Swal.fire({
                icon: "info",
                title: `<div style='text-align:center;'>${t('UI_TEXT.NO_PARTS_FOUND')}</div>`,
                html: `<div style='text-align:center;'>${t('UI_TEXT.THERE_ARE_NO_RSPL_PARTS_FOR_TYPE', { type })}</div>`
            });
            return;
        }

        const purchasable = canShowAddToCart ? parts.filter(p => p.purchasable) : [];
        const requestable = canShowAddToRequestList ? parts.filter(p => !p.purchasable) : [];

        Swal.fire({
            icon: "question",
            title: `<div style='text-align:center;'>${requestable.length > 0 ? mixedRsplMessage : `Add All ${type} Parts?`}</div>`,

            html: `
            <div style='text-align:center;'>
                <b>Purchasable:</b> ${purchasable.length}<br/>
                <b>Request List:</b> ${requestable.length}<br/><br/>
                ${requestable.length > 0
                    ? ''
                    : t('UI_TEXT.ALL_SELECTED_PARTS_WILL_BE_ADDED_AS_LINE_ITEMS', {
                        defaultValue: 'All selected parts will be added as individual line items.'
                    })}
            </div>
        `,
            showCancelButton: true,
            confirmButtonText: t('TABLE_HEADERS.ADD'),
            cancelButtonText: t('BUTTON.CANCEL')
        }).then((result) => {
            if (!result.isConfirmed) return;

            const normalizedParts = parts
                .map((part) => normalizePartMeta({
                    ...part,
                    part_number: part?.product_number,
                    product_id: part?.product_id,
                    price: getSitePrice(part),
                    purchasable: part?.purchasable,
                }))
                .filter((part) => !!part?.part_number && !!part?.product_id);

            const purchasableParts = normalizedParts.filter((part) => !!part.purchasable);
            const requestableParts = normalizedParts.filter((part) => !part.purchasable);

            const existingCart = new Set(cartItems.map((item) => String(item?.part_number || "").trim()));
            const existingRequest = new Set(requestItems.map((item) => String(item?.part_number || "").trim()));

            const cartToAdd = purchasableParts
                .filter((item) => {
                    const key = String(item?.part_number || "").trim();
                    if (!key || existingCart.has(key)) return false;
                    existingCart.add(key);
                    return true;
                })
                .map((item) => ({ ...item, qty: 1 }));

            const requestToAdd = requestableParts.filter((item) => {
                const key = String(item?.part_number || "").trim();
                if (!key || existingRequest.has(key)) return false;
                existingRequest.add(key);
                return true;
            });

            const cartAdded = cartToAdd.length;
            const requestAdded = requestToAdd.length;

            if (canShowAddToCart && cartToAdd.length > 0) {
                setCartItems((prev) => [...prev, ...cartToAdd]);
            }
            if (canShowAddToRequestList && requestToAdd.length > 0) {
                setRequestItems((prev) => [...prev, ...requestToAdd]);
            }

            Swal.fire({
                icon: "success",
                title: `<div style='text-align:center;'>${t('BUTTON.ADDED')}</div>`,
                html: `
                <div style='text-align:center;'>
                    <b>${t('UI_TEXT.PURCHASABLE_PARTS')}:</b> ${cartAdded}<br/>
                    <b>${t('UI_TEXT.NON_PURCHASABLE_PARTS')}:</b> ${requestAdded}
                </div>
                `,
                confirmButtonText: t('GENERAL.OK'),
            });
        });
    };





    // -------------------- CART + REQUEST HELPERS -------------------- //

    // CLEAR BOTH LISTS
    const handleClearAll = () => {
        setCartItems([]);
        setRequestItems([]);

        // Optional: Close drawer or show toast message

    };

    const handleClearCart = () => {
        setCartItems([]);
    };

    const handleClearRequestList = () => {
        setRequestItems([]);
    };


    // ORDER ITEMS HANDLER
    const handleOrderItems = async () => {
        if (orderingItems) return;

        if (cartItems.length === 0 && requestItems.length === 0) {
            Swal.fire({
                icon: "info",
                title: `<div style='text-align:center;width:100%;'>${t('GENERAL.NO_ITEMS_TO_ORDER')}</div>`,
                html: `<div style='text-align:center;width:100%;'>${t('GENERAL.CART_AND_REQUEST_LIST_ARE_EMPTY')}</div>`,
            });
            return;
        }
        setOrderingItems(true);
        try {

            const expandedCart = [];
            const expandedRequestList = [];

            const expandGroup = (group) => {
                const type = group.group_type;
                const typeKey = normalizeRsplType(type);
                const parts = Array.isArray(productsByType[typeKey]) ? productsByType[typeKey] : [];
                if (!parts.length) return;

                parts.forEach((p) => {
                    const item = {
                        product_id: p.product_id,
                        part_number: p.product_number,
                    };

                    if (p.purchasable) {
                        expandedCart.push({
                            ...item,
                            price: getSitePrice(p),
                            qty: 1,
                        });
                    } else {
                        expandedRequestList.push(item);
                    }
                });
            };

            cartItems.forEach((item) => {
                if (item.is_group) {
                    expandGroup(item);
                } else {
                    expandedCart.push({
                        product_id: item.product_id,
                        part_number: item.part_number,
                        qty: item.qty,
                        price: item.price,
                    });
                }
            });

            requestItems.forEach((item) => {
                if (item.is_group) {
                    expandGroup(item);
                } else {
                    expandedRequestList.push({
                        product_id: item.product_id,
                        part_number: item.part_number,
                    });
                }
            });

            const cartByProductId = new Map();
            expandedCart
                .filter((item) => item?.product_id)
                .forEach((item) => {
                    const productId = String(item.product_id);
                    const qty = Number(item?.qty ?? 1) || 1;
                    cartByProductId.set(productId, (cartByProductId.get(productId) || 0) + qty);
                });

            const requestByProductId = new Set(
                expandedRequestList
                    .filter((item) => item?.product_id)
                    .map((item) => String(item.product_id))
            );

            const rlProducts = Array.from(requestByProductId).map((productId) => `${productId}:1`).join(",");
            const cartProducts = Array.from(cartByProductId.entries()).map(([productId, qty]) => `${productId}:${qty}`).join(",");

            if (!rlProducts && !cartProducts) {
                Swal.fire({
                    icon: "info",
                    title: "<div style='text-align:center;width:100%;'>No Orderable Parts</div>",
                    html: "<div style='text-align:center;width:100%;'>The selected items could not be translated into orderable products.</div>",
                });
                return;
            }

            let storedUser = null;
            try {
                const rawUser = sessionStorage.getItem("user");
                storedUser = rawUser ? JSON.parse(rawUser) : null;
            } catch (storageError) {
                console.error("Unable to read session user:", storageError);
            }

            const userId = user?.user_id || storedUser?.user_id;

            if (!userId || !selectedSite || !machine_id) {
                Swal.fire({
                    icon: "warning",
                    title: "<div style='text-align:center;width:100%;'>Missing Information</div>",
                    html: "<div style='text-align:center;width:100%;'>User, site, or machine information is unavailable. Please refresh and try again.</div>",
                });
                return;
            }

            const requestPayload = {
                userId,
                selectedSite,
                AddToRequest: rlProducts,
                AddToCart: cartProducts,
                machine_id,
            };




            try {
                const response = await addModelCartItems(requestPayload);

                if (response?.status === false) {
                    throw new Error(response?.message || "Unable to process order.");
                }

                handleClearAll();
                setCartOpen(false);

                const destination = cartProducts ? "/cart" : "/requests";
                const destinationLabel = cartProducts ? "Cart" : "Request List";
                const totalCartItems = expandedCart.length;
                const totalRequestItems = expandedRequestList.length;
                const redirectSeconds = 5;
                let remainingSeconds = redirectSeconds;
                let countdownInterval = null;

                const redirectFromSwal = () => {
                    if (destination === "/cart") {
                        const params = new URLSearchParams({
                            mvSource: "modelviewer",
                            mvCartCount: String(totalCartItems),
                            mvRequestCount: String(totalRequestItems),
                        });
                        const url = `${destination}?${params.toString()}`;
                        window.location.href = url;
                    } else {
                        navigate(destination);
                    }
                };

                Swal.fire({
                    icon: "success",
                    title: "<div style='text-align:center;width:100%;'>Items Submitted</div>",
                    html: `
                    <div style='text-align:center;width:100%;'>
                        <b>Added to Cart:</b> ${totalCartItems}<br/>
                        <b>Added to Request List:</b> ${totalRequestItems}<br/><br/>
                        Redirecting to ${destinationLabel} in <b><span id="swal-cart-countdown">${remainingSeconds}</span>s</b>.
                    </div>
                `,
                    showConfirmButton: false,
                    allowOutsideClick: false,
                    didOpen: () => {
                        const countdownEl = document.getElementById("swal-cart-countdown");
                        countdownInterval = setInterval(() => {
                            remainingSeconds -= 1;
                            if (remainingSeconds <= 0) {
                                clearInterval(countdownInterval);
                                countdownInterval = null;
                                Swal.close();
                                return;
                            }
                            if (countdownEl) {
                                countdownEl.textContent = remainingSeconds;
                            }
                        }, 1000);
                    },
                    willClose: () => {
                        if (countdownInterval) {
                            clearInterval(countdownInterval);
                            countdownInterval = null;
                        }
                    }
                }).then(() => {
                    redirectFromSwal();
                });
            } catch (error) {
                console.error("Error placing model order:", error);
                Swal.fire({
                    icon: "error",
                    title: "<div style='text-align:center;width:100%;'>Unable to Submit</div>",
                    html: `<div style='text-align:center;width:100%;'>${error?.message || "Please try again later."}</div>`,
                });
            }
        } finally {
            setOrderingItems(false);
        }
    };



    // useEffect(() => {
    //     if (error === "Model Not Found") {
    //         const timer = setTimeout(() => {
    //             window.location.href = "/machines";
    //         }, 3000);

    //         return () => clearTimeout(timer);
    //     }
    // }, [error]);

    if (error === "Model Not Found") {
        return (
            <Box
                sx={{
                    height: "100vh",
                    width: '100vw',
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    px: 3,
                }}
            >
                <Box sx={{ fontSize: 80, color: "error.main", opacity: 0.9 }}>
                    <InfoOutlined fontSize="inherit" />
                </Box>

                <Typography variant="h4" fontWeight="bold" mt={2}>
                    Model Not Found
                </Typography>

                <Typography variant="body1" color="text.secondary" mt={1} maxWidth={420}>
                    The requested 3D model could not be located. It may not exist or may have been removed.
                </Typography>

                <Button
                    variant="contained"
                    size="large"
                    sx={{ mt: 2, px: 4 }}
                    onClick={() => navigate(returnPath)}
                >
                    Back to Machines
                </Button>
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    position: 'relative',
                    height: '100vh',
                    width: '100vw',
                    overflow: 'hidden',
                }}
                onContextMenu={handleCanvasContextMenu}
            >
                {modelFilesExist && !error ? (
                    <>
                        <Canvas
                            camera={{ position: [0, 0, 5], fov: 45 }}
                            style={{ width: '100%', height: '100%', display: modelVisible ? "inherit" : "none" }}
                            onPointerMissed={handleCanvasPointerMissed}
                        >
                            <Suspense fallback={null}>
                                <ModelScene
                                    ref={sceneRef}
                                    glbPath={`${SERVER_BASE_URL}/stock-models/GLB/Models/${fileName}.glb`}
                                    assemblyPath={`${SERVER_BASE_URL}/stock-models/GLB/Boms/${fileName}.json`}
                                    onPartSelected={handlePartSelected}
                                    onPartDoubleClick={handlePartDoubleClickHide}
                                    onAssemblyMapped={setAssemblyTree}
                                    lightSettings={{ type: 'studio', intensity: settings.light }}
                                    onSelectionChange={setSelectedParts}
                                    onVisibilityChange={handleVisibilityChange}
                                    resolveSelectionNode={resolveNearestOrderableSelectionNode}
                                    explodeFactor={explodeEnabled ? explodeValue : 0}
                                    onModelLoaded={() => setModelLoaded(true)}
                                    navMode={navMode}
                                    highlightColor={defaultHighlightColor}
                                    highlightOpacity={defaultHighlightOpacity}
                                    modelAxis={defaultUpAxis}
                                    modelOrientation={modelOrientation}
                                    defaultCameraCorner={defaultCameraCorner}
                                />
                            </Suspense>

                            {settings.showGizmo && (
                                <GizmoHelper key={`gizmo-${defaultUpAxis}-${modelOrientation?.left || 'legacy'}-${modelOrientation?.right || 'legacy'}`} alignment="bottom-left" margin={[80, 80]}>
                                    <GizmoViewcube />
                                    <GizmoViewport
                                        axisColors={['red', 'green', 'blue']}
                                        labels={gizmoAxisLabels}
                                        labelColor="black"
                                    />
                                </GizmoHelper>
                            )}
                        </Canvas>

                    </>
                ) : null}
                {/* 3D Canvas – always mounted so OrbitControls stay stable */}

                {non3DMessageVisible && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            background: "rgba(255,255,255,0.92)",
                            p: 2.5,
                            borderRadius: "10px",
                            boxShadow: "0 0 8px rgba(0,0,0,0.25)",
                            zIndex: 100
                        }}
                    >
                        <Typography variant="body1" sx={{ textAlign: "center" }}>
                            <b>{partMenuData?.part_number || non3DPartNumber}</b> is not available in this 3D view. Please see another split model view.
                        </Typography>
                    </Box>
                )}



                {/* Loading / error overlay (blocks interaction only while visible) */}
                {(loading || error) && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 999,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            background: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? 'rgba(0,0,0,0.8)'
                                    : 'rgba(255,255,255,1)',
                        }}
                    >
                        {error ? (
                            <Box sx={containerStyles}>
                                <Typography
                                    color="error"
                                    variant="h6"
                                    mb={1}
                                >
                                    {error}
                                </Typography>
                                <Typography variant="body2">
                                    Please try refreshing the page or going
                                    back.
                                </Typography>
                            </Box>
                        ) : (
                            <LoadingBoxes
                                text={loadingMessages[loadingStep]}
                                subText="Note: The 3D model may take up to a minute to fully load."
                                percentage={Math.round(percentage)}
                            />
                        )}
                    </Box>
                )}

                {/* Floating UI overlays – only actual boxes have pointerEvents:auto */}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                    }}
                >
                    {/* Title (top center) */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 16,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 10,
                            pointerEvents: 'auto',
                        }}
                    >
                        <PageTitle
                            title={`${machineNumber || ''} | ${machineName || ''
                                }`}
                        />
                    </Box>

                    {/* Left menu toggle + panel */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 16,
                            left: 24,
                            display: 'flex',
                            flexDirection: 'column',
                            rowGap: 2,
                            pointerEvents: 'auto',
                        }}
                    >
                        <Tooltip
                            title={showMenuBox ? 'Close Menu' : 'Open Menu'}
                            arrow
                            placement="right"
                        >
                            <Button
                                sx={{
                                    background: showMenuBox
                                        ? (theme) =>
                                            theme.palette.primary.main
                                        : '#fff',
                                    color: !showMenuBox
                                        ? (theme) =>
                                            theme.palette.primary.main
                                        : '#fff',
                                }}
                                variant="modelMenuButtons"
                                onClick={() =>
                                    setShowMenuBox((prev) => !prev)
                                }
                            >
                                <MenuIcon />
                            </Button>
                        </Tooltip>

                        {showMenuBox && (
                            <Box
                                sx={{
                                    height: '100%',
                                    minHeight: '80vh',
                                    maxHeight: '80vh',
                                    display: 'flex',
                                    width: '450px',
                                    flexDirection: 'column',
                                    rowGap: 0,
                                    backgroundColor:
                                        'rgba(255, 255, 255, 0.8)',
                                    boxShadow:
                                        '0 0 6px 1px rgba(0,0,0,0.1)',
                                    borderRadius: '10px',
                                    overflow: 'hidden',
                                }}
                            >
                                <Box sx={{ minHeight: '38px', width: '100%' }}>
                                    <Tabs
                                        value={tabValue}
                                        onChange={handleTabChange}
                                        variant="fullWidth"
                                        textColor="primary"
                                        indicatorColor="primary"
                                        sx={{
                                            borderBottom:
                                                '1px solid rgba(0,0,0,0.1)',
                                        }}
                                    >
                                        <Tab label={menuTabLabels.subAssemblies} />
                                        <Tab label={menuTabLabels.rspl} />
                                        <Tab label={menuTabLabels.partsTree} />
                                    </Tabs>
                                </Box>

                                {/* Sub-Assemblies */}
                                {tabValue === 0 && (
                                    <TabPanel
                                        value={tabValue}
                                        index={0}
                                        title={menuTabLabels.subAssemblies}
                                        subtitle={showMenuTabSubtitles ? "Level 1 subassemblies from the assembly tree." : ""}
                                        headerActions={
                                            <Tooltip title="Scroll to Top">
                                                <IconButton
                                                    size="small"
                                                    onClick={scrollSubAssembliesToTop}
                                                >
                                                    <VerticalAlignTop fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                rowGap: 1.5,
                                                width: '100%',
                                                flex: 1,
                                                minHeight: 0,
                                            }}
                                        >
                                            <SearchInput
                                                value={subSearchTerm}
                                                onChange={(e) =>
                                                    setSubSearchTerm(
                                                        e.target.value
                                                    )
                                                }
                                                onSubmit={() =>
                                                    setSubSearchTerm((v) => v)
                                                }
                                            />

                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.secondary',
                                                    fontSize: '10px',
                                                }}
                                            >
                                                Click a subassembly to hide all
                                                others and show only that
                                                subassembly. Use Ctrl / Cmd +
                                                click to multi-select.
                                            </Typography>
                                            <Box
                                                ref={subAssembliesScrollRef}
                                                sx={{
                                                    flex: 1,
                                                    minHeight: 0,
                                                    overflowY: 'auto',
                                                    p: 1,
                                                }}
                                            >

                                                {filteredSubAssemblies.length ===
                                                    0 && (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: 'text.secondary',
                                                                fontSize: 12,
                                                            }}
                                                        >
                                                            No subassemblies found.
                                                        </Typography>
                                                    )}

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1,
                                                    }}
                                                >
                                                    {filteredSubAssemblies.map(
                                                        ({ id, node }) => {
                                                            const isActive =
                                                                selectedSubAssemblyIds.includes(
                                                                    id
                                                                );
                                                            const matched = findMatchingProductForPart(
                                                                node?.name,
                                                                node?.productRef
                                                            );
                                                            return (
                                                                <Box
                                                                    onClick={(e) => handleSubAssemblyCardClick(e, id, node)}
                                                                    sx={{ cursor: "pointer" }}
                                                                >

                                                                    <PartCard
                                                                        data={{
                                                                            part_name: matched?.product_name || node.name,
                                                                            part_number: matched?.product_number ?? node.name ?? "N/A",
                                                                            purchasable: matched?.purchasable ?? 0,
                                                                            price: matched ? getSitePrice(matched) : null,
                                                                            productImageURL: matched?.productImageURL ?? null

                                                                        }}
                                                                        isActive={selectedSubAssemblyIds.includes(id)}
                                                                        showPrice={canShowPrice}
                                                                    />

                                                                </Box>

                                                            );
                                                        }
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </TabPanel>
                                )}

                                {/* RSPL Parts */}
                                {tabValue === 1 && (
                                    <TabPanel
                                        value={tabValue}
                                        index={1}
                                        title={menuTabLabels.rspl}
                                        subtitle={showMenuTabSubtitles ? "List RSPL parts from products with their mapped types." : ""}
                                        headerActions={
                                            <Tooltip title="Scroll to Top">
                                                <IconButton
                                                    size="small"
                                                    onClick={scrollRsplToTop}
                                                >
                                                    <VerticalAlignTop fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                rowGap: 1.5,
                                                width: '100%',
                                                flex: 1,
                                                minHeight: 0,
                                            }}
                                        >
                                            <SearchInput
                                                value={rsplSearchTerm}
                                                onChange={(e) => setRsplSearchTerm(e.target.value)}
                                                onSubmit={handleRsplSearchSubmit}
                                            />


                                            <Box
                                                ref={rsplScrollRef}
                                                sx={{
                                                    flex: 1,
                                                    minHeight: 0,
                                                    overflowY: 'auto',
                                                    px: 1,
                                                }}
                                            >
                                                {rsplLoading && (
                                                    <Box
                                                        sx={{
                                                            height: "300px",
                                                            display: "flex",
                                                            flexDirection: 'column',
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: '100%',
                                                            rowGap: 2
                                                        }}
                                                    >
                                                        <CircularProgress size={40} />
                                                        <Typography variant='body1' color='text.secondary'>Loading RSPL Parts...</Typography>
                                                    </Box>
                                                )}
                                                {rsplReady && (<>
                                                    {/* INNER TABS FOR PRODUCT TYPES */}
                                                    <Tabs
                                                        value={innerTab}
                                                        onChange={handleRsplTypeChange}
                                                        sx={{ borderBottom: 1, borderColor: "divider", mb: 0.75 }}
                                                    >
                                                        {["All", ...productTypes].map((type, idx) => (
                                                            <Tab key={idx} label={type} value={type} />
                                                        ))}
                                                    </Tabs>


                                                    {["All", ...productTypes].map((type, idx) => (
                                                        <Box key={idx} role="tabpanel" hidden={innerTab !== type}>
                                                            {innerTab === type && (
                                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                                                    {rsplRenderedByType[type]}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    ))}


                                                </>)}



                                            </Box>
                                            <Box
                                                sx={{
                                                    mt: 2,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    columnGap: 2,
                                                    color: "text.secondary",
                                                    fontSize: "11px"
                                                }}
                                            >

                                                {(canShowAddToCart || canShowAddToRequestList) && productTypes.map((type, idx) => (

                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        sx={{ mb: 1 }}
                                                        onClick={() => handleAddAllProductType(type)}
                                                    >
                                                        Add All {type} Parts
                                                    </Button>


                                                ))}
                                            </Box>



                                        </Box>
                                    </TabPanel>
                                )}

                                {/* Parts Tree */}
                                {tabValue === 2 && (
                                    <TabPanel
                                        value={tabValue}
                                        index={2}
                                        title={menuTabLabels.partsTree}
                                        subtitle={showMenuTabSubtitles ? "Assembly tree derived from model." : ""}
                                        headerActions={
                                            <>
                                                <Tooltip title="Expand All">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={expandAllTreeNodes}
                                                        >
                                                            <FiChevronsDown size={18} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Collapse All">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={collapseAllTreeNodes}
                                                        >
                                                            <FiChevronsUp size={18} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Scroll to Top">
                                                    <IconButton
                                                        size="small"
                                                        onClick={scrollPartsTreeToTop}
                                                    >
                                                        <VerticalAlignTop fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                rowGap: 1.5,
                                                width: '100%',
                                                flex: 1,
                                                minHeight: 0,
                                            }}
                                        >
                                            <SearchInput
                                                value={treeSearchTerm}
                                                onChange={(e) =>
                                                    setTreeSearchTerm(
                                                        e.target.value
                                                    )
                                                }
                                                onSubmit={
                                                    handleTreeSearchSubmit
                                                }
                                                placeholder={`Search in ${menuTabLabels.partsTree}`}
                                            />
                                            <Box
                                                ref={partsTreeScrollRef}
                                                sx={{
                                                    flex: 1,
                                                    minHeight: 0,
                                                    overflowY: 'auto',
                                                }}
                                            >
                                                {filteredTree ? (
                                                    renderTreeItems(
                                                        filteredTree,
                                                        0
                                                    )
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            mt: 2,
                                                            textAlign:
                                                                'center',
                                                        }}
                                                    >
                                                        Assembly tree not
                                                        loaded yet.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TabPanel>
                                )}
                            </Box>
                        )}
                    </Box>

                    {/* Top-right close (just a placeholder button for now) */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 24,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            pointerEvents: 'auto',
                        }}
                    >
                        <Tooltip title="Refresh Page">
                            <IconButton onClick={handleHardRefresh}>
                                <Autorenew />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t("UI_TEXT.TOOLTIP_CLOSE_MODEL_VIEW")}>
                            <IconButton onClick={() => { handleCloseModel() }}>
                                <Close />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Bottom-left disclaimer */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: '2px',
                            left: '5px',

                            maxWidth: 'calc(100% - 122px)',
                            pointerEvents: 'auto',
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "11px", lineHeight: 1.35 }}
                        >
                            Disclaimer: Some drawings may contain parts that may not appear on the machine&apos;s 2D or 3D model. When viewing split models of the machine, the Sub Assemblies, RSPL Parts, and BOM may also include parts from other splits of the machine.
                        </Typography>
                    </Box>

                    {/* Bottom-center toolbar + explode slider */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 25,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 10,
                            pointerEvents: 'auto',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            {/* Explode slider */}
                            {explodeEnabled && (
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        borderRadius: '999px',
                                        backgroundColor:
                                            'rgba(255,255,255,0.9)',
                                        boxShadow:
                                            '0 0 6px 1px rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        columnGap: 2,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{ minWidth: 60 }}
                                    >
                                        Explode
                                    </Typography>
                                    <Slider
                                        value={explodeValue}
                                        min={0}
                                        max={1}
                                        step={0.05}
                                        onChange={(_, val) => {
                                            setExplodeValue(val);
                                            setSettings((prev) => ({
                                                ...prev,
                                                explodeValue: val,
                                            }));
                                        }}
                                        sx={{ width: 200 }}
                                    />
                                </Box>
                            )}

                            {/* Toolbar row */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: 2.5,
                                    flexWrap: 'wrap',
                                }}
                            >
                                {/* Group 1: Home */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tooltip title={t("UI_TEXT.TOOLTIP_HOME_RESET_VIEW")}>
                                        <Button variant="modelMenuButtons" onClick={resetModel}>
                                            <HomeIcon />
                                        </Button>
                                    </Tooltip>
                                </Box>

                                {/* Group 2: Navigation + explode + zoom selected */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tooltip title={navMode === "pan" ? "Rotate Mode" : "Pan Mode"}>
                                        <Button
                                            variant="modelMenuButtons"
                                            onClick={() => {
                                                const nextMode = navMode === "pan" ? "rotate" : "pan";
                                                setNavMode(nextMode);
                                                setSettings((prev) => ({ ...prev, navMode: nextMode }));
                                            }}
                                            sx={(theme) =>
                                                navMode === "pan"
                                                    ? {
                                                        backgroundColor: theme.palette.primary.main,
                                                        color: "#fff",
                                                    }
                                                    : {}
                                            }
                                        >
                                            {navMode === "pan" ? <Loop /> : <OpenWithIcon />}
                                        </Button>
                                    </Tooltip>

                                    <Tooltip title={explodeEnabled ? "Disable Explode Model" : "Explode Model"}>
                                        <Button
                                            variant="modelMenuButtons"
                                            onClick={() =>
                                                setExplodeEnabled((prev) => {
                                                    const next = !prev;
                                                    setSettings((settingsPrev) => ({
                                                        ...settingsPrev,
                                                        explodeEnabled: next,
                                                    }));
                                                    return next;
                                                })
                                            }
                                            sx={(theme) =>
                                                explodeEnabled
                                                    ? {
                                                        backgroundColor: theme.palette.primary.main,
                                                        color: '#fff',
                                                    }
                                                    : {}
                                            }
                                        >
                                            <OpenInFull />
                                        </Button>
                                    </Tooltip>

                                    <Tooltip title={selectedPart ? "Zoom to Selected" : "Zoom to Selected (select a part first)"}>
                                        <span>
                                            <Button
                                                variant="modelMenuButtons"
                                                disabled={!selectedPart}
                                                onClick={() => sceneRef.current?.zoomToSelectionToggle?.()}
                                            >
                                                <ZoomInIcon />
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Box>

                                {/* Group 3: Text toggles */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tooltip title={machineTransparentToggle ? "Switch to Machine Solid" : "Switch to Machine Transparent"}>
                                        <Button
                                            variant="modelMenuButtons"
                                            onClick={handleMachineTransparencyToggle}
                                            sx={(theme) => ({
                                                width: 'auto',
                                                minWidth: 'fit-content',
                                                px: 1.5,
                                                borderRadius: '15px',
                                                textTransform: "none",
                                                whiteSpace: 'nowrap',
                                                overflow: 'visible',
                                                gap: 0.5,
                                                ...(machineTransparentToggle
                                                    ? {
                                                        backgroundColor: theme.palette.primary.main,
                                                        color: "#fff",
                                                    }
                                                    : {}),
                                            })}
                                        >
                                            Machine
                                            {machineTransparentToggle ? <BsTransparency size={14} /> : <PiDropSimpleFill size={14} />}
                                        </Button>
                                    </Tooltip>

                                    <Tooltip
                                        title={
                                            !selectedPart
                                                ? "Part transparency requires a selected part"
                                                : (partTransparencyToggle ? "Switch selected part to Solid" : "Switch selected part to Transparent")
                                        }
                                    >
                                        <span>
                                            <Button
                                                variant="modelMenuButtons"
                                                disabled={!selectedPart}
                                                onClick={handlePartTransparencyToggle}
                                                sx={(theme) => ({
                                                    width: 'auto',
                                                    minWidth: 'fit-content',
                                                    px: 1.5,
                                                    borderRadius: '15px',
                                                    textTransform: "none",
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'visible',
                                                    gap: 0.5,
                                                    ...(partTransparencyToggle
                                                        ? {
                                                            backgroundColor: theme.palette.primary.main,
                                                            color: "#fff",
                                                        }
                                                        : {}),
                                                })}
                                            >
                                                Part
                                                {partTransparencyToggle ? <BsTransparency size={14} /> : <PiDropSimpleFill size={14} />}
                                            </Button>
                                        </span>
                                    </Tooltip>

                                    <Tooltip
                                        title={
                                            !selectedPart
                                                ? "Isolate requires a selected part"
                                                : (isolateSelectionToggle ? "Disable Isolate" : "Isolate selected part")
                                        }
                                    >
                                        <span>
                                            <Button
                                                variant="modelMenuButtons"
                                                disabled={!selectedPart}
                                                onClick={handleIsolateToggle}
                                                endIcon={<NorthEast fontSize="small" />}
                                                sx={(theme) => ({
                                                    width: 'auto',
                                                    minWidth: 'fit-content',
                                                    px: 1.5,
                                                    borderRadius: '15px',
                                                    textTransform: "none",
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'visible',
                                                    ...(isolateSelectionToggle
                                                        ? {
                                                            backgroundColor: theme.palette.primary.main,
                                                            color: "#fff",
                                                        }
                                                        : {}),
                                                })}
                                            >
                                                Isolate
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Box>

                                {/* Group 4: Selection + parent */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tooltip title={selectVisibleActive ? "Unselect Visible Parts" : "Select Visible Parts"}>
                                        <span>
                                            <Button
                                                variant="modelMenuButtons"
                                                onClick={handleSelectVisibleParts}
                                                sx={(theme) =>
                                                    selectVisibleActive
                                                        ? {
                                                            backgroundColor: theme.palette.primary.main,
                                                            color: "#fff",
                                                        }
                                                        : {}
                                                }
                                            >
                                                <CheckBoxIcon />
                                            </Button>
                                        </span>
                                    </Tooltip>

                                    <Tooltip title={selectedPart ? "Show Parent in View" : "Show Parent requires a selected part"}>
                                        <span>
                                            <Button
                                                variant="modelMenuButtons"
                                                disabled={!selectedPart}
                                                onClick={showParentFromSelection}
                                            >
                                                <KeyboardDoubleArrowUp />
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Box>

                                {/* Group 5: Help + more */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tooltip title={t("UI_TEXT.TOOLTIP_HELP")}>
                                        <Button variant="modelMenuButtons" onClick={handleHelpClick}>
                                            <QuestionMark />
                                        </Button>
                                    </Tooltip>

                                    <Tooltip title={t("UI_TEXT.TOOLTIP_MORE")}>
                                        <Button variant="modelMenuButtons" onClick={handleMoreClick}>
                                            <MoreVertIcon />
                                        </Button>
                                    </Tooltip>
                                </Box>

                                <Menu
                                    anchorEl={moreAnchorEl}
                                    open={isMoreMenuOpen}
                                    onClose={handleMoreClose}
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'left',
                                    }}
                                    transformOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    }}
                                    sx={{ mt: -1 }}
                                >
                                    {/* <MenuItem
                                            onClick={() => setUpAxis('Z_UP')}
                                            selected={upAxis === 'Z_UP'}
                                        >
                                            Z Up
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() => setUpAxis('Y_UP')}
                                            selected={upAxis === 'Y_UP'}
                                        >
                                            Y Up
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() => setUpAxis('X_UP')}
                                            selected={upAxis === 'X_UP'}
                                        >
                                            X Up
                                        </MenuItem> */}

                                    <MenuItem onClick={handleBom}>
                                        <ListItemIcon>
                                            <TableChartIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>BOM</ListItemText>
                                    </MenuItem>
                                    {/* <MenuItem onClick={handleSettings}>
                                            <ListItemIcon>
                                                <SettingsIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>
                                                Settings
                                            </ListItemText>
                                        </MenuItem> */}
                                </Menu>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Cart drawer */}
                {cartOpen && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 160,
                            right: 35,
                            zIndex: 25,
                            height: '100%',
                            minHeight: '75vh',
                            maxHeight: '75vh',
                            display: 'flex',
                            width: '500px',
                            flexDirection: 'column',
                            rowGap: 0,
                            backgroundColor:
                                'rgba(255, 255, 255, 0.8)',
                            boxShadow: '0 0 6px 1px rgba(0,0,0,0.1)',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            p: 2,
                            pointerEvents: 'auto',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>

                            <Box>
                                <Typography
                                    fontWeight="bold"
                                    variant="body1"
                                    mb={0}
                                >
                                    Cart
                                </Typography>
                                <Typography
                                    variant="body2"
                                    fontSize="11px"
                                    mb={1}
                                >
                                    List of all the Parts in Cart
                                </Typography>
                            </Box>
                            <Button
                                variant="text"
                                color="warning"
                                onClick={handleClearCart}
                            >
                                Clear Cart
                            </Button>
                        </Box>
                        <Box
                            sx={{
                                flexGrow: 1,
                                overflowY: 'auto',
                                minHeight: '200px',
                                p: 1,
                            }}
                        >
                            <Table size="small" sx={{ fontSize: 12 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontSize: 12 }}><b>{t('UI_TEXT.PART')}</b></TableCell>
                                        <TableCell sx={{ fontSize: 12 }} align="center"><b>Qty</b></TableCell>
                                        {canShowPrice && <TableCell sx={{ fontSize: 12 }}><b>Price</b></TableCell>}
                                        <TableCell sx={{ fontSize: 12 }}><b>Action</b></TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {cartItems.map((item) => (
                                        <TableRow key={item.part_number} sx={{ fontSize: 12 }}>

                                            <TableCell sx={{ maxWidth: 150, fontSize: 12 }}>
                                                <Typography
                                                    noWrap
                                                    sx={{
                                                        fontSize: 12,
                                                        textOverflow: "ellipsis",
                                                        overflow: "hidden"
                                                    }}
                                                >
                                                    {item.part_number}
                                                </Typography>
                                            </TableCell>

                                            <TableCell sx={{ fontSize: 12 }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        gap: 1,
                                                        fontSize: 12
                                                    }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => decrementQty(item.part_number)}
                                                        sx={{ fontSize: 12 }}
                                                    >
                                                        −
                                                    </IconButton>

                                                    <span style={{ fontSize: 12 }}>{item.qty}</span>

                                                    <IconButton
                                                        size="small"
                                                        onClick={() => incrementQty(item.part_number)}
                                                        sx={{ fontSize: 12 }}
                                                    >
                                                        +
                                                    </IconButton>
                                                </Box>
                                            </TableCell>

                                            {canShowPrice && (
                                                <TableCell sx={{ fontSize: 12 }}>
                                                    {item.price}
                                                </TableCell>
                                            )}

                                            <TableCell sx={{ fontSize: 12 }}>
                                                <IconButton
                                                    color="error"
                                                    onClick={() => removeCartItem(item.part_number)}
                                                    sx={{ fontSize: 12 }}
                                                >
                                                    <Delete sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </TableCell>

                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>



                        </Box>
                        {canShowAddToRequestList && (
                            <>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>

                            <Box>

                                <Typography
                                    fontWeight="bold"
                                    variant="body1"
                                    mb={0}
                                >
                                    Request List
                                </Typography>
                                <Typography
                                    variant="body2"
                                    fontSize="11px"
                                    mb={1}
                                >
                                    List of all the Parts in Request List
                                </Typography>
                            </Box>
                            <Button
                                variant="text"
                                color="warning"
                                onClick={handleClearRequestList}
                            >
                                Clear Request List
                            </Button>
                        </Box>
                        <Box
                            sx={{
                                flexGrow: 1,
                                overflowY: 'auto',
                                minHeight: '200px',
                                p: 1,
                            }}
                        >

                            <Table size="small" sx={{ fontSize: 12 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontSize: 12 }}><b>{t('UI_TEXT.PART')}</b></TableCell>
                                        <TableCell sx={{ fontSize: 12 }}><b>Action</b></TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {requestItems.map((item) => (
                                        <TableRow key={item.part_number} sx={{ fontSize: 12 }}>

                                            <TableCell sx={{ maxWidth: 170, fontSize: 12 }}>
                                                <Typography
                                                    noWrap
                                                    sx={{
                                                        fontSize: 12,
                                                        textOverflow: "ellipsis",
                                                        overflow: "hidden"
                                                    }}
                                                >
                                                    {item.part_number}
                                                </Typography>
                                            </TableCell>

                                            <TableCell sx={{ fontSize: 12 }}>
                                                <IconButton
                                                    color="error"
                                                    onClick={() => removeRequestItem(item.part_number)}
                                                    sx={{ fontSize: 12 }}
                                                >
                                                    <Delete sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </TableCell>

                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>


                        </Box>
                            </>
                        )}

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                                alignItems: 'center',
                                mb: 1.25,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    columnGap: 1.25,
                                    alignItems: 'center',
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setCartOpen(false);
                                    }}
                                >
                                    {t("BUTTON.CLOSE")}

                                </Button>


                            </Box>
                            {(canShowAddToCart || canShowAddToRequestList) && (
                                <Button variant="contained" onClick={handleOrderItems} disabled={orderingItems}>
                                    {orderingItems ? "Processing..." : "Order Items"}
                                </Button>
                            )}
                        </Box>
                        <Typography
                            variant="body2"
                            fontSize="10px"
                            color="text.secondary"
                        >
                            <b>Note:</b> The Items in the above Cart /
                            Request List are specific to this model.
                        </Typography>
                    </Box>
                )}

                {/* Cart FAB */}
                {(canShowAddToCart || canShowAddToRequestList) && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 100,
                            right: 35,
                            zIndex: 10,
                            pointerEvents: 'auto',
                        }}
                    >
                    <Badge
                        badgeContent={cartItems.length}
                        color="error"
                        showZero
                        overlap="circular"
                    >
                        <Box
                            onClick={handleCartClick}
                            sx={{
                                width: 50,
                                height: 50,
                                bgcolor: (theme) =>
                                    theme.palette.primary.main,
                                color: '#fff',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    bgcolor: (theme) =>
                                        theme.palette.primary.dark,
                                    transform: 'scale(1.05)',
                                },
                                '&:active': {
                                    transform: 'scale(0.95)',
                                },
                            }}
                        >
                            <ShoppingCart />
                        </Box>
                    </Badge>
                    </Box>
                )}

                {/* Part menu (draggable) */}
                {showPartMenu && partMenuData && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 80,
                            right: 30,
                            zIndex: 30,
                            transform: `translate(${partMenuDelta.x}px, ${partMenuDelta.y}px)`,
                            pointerEvents: 'auto',
                        }}
                    >
                        <PartMenu
                            onHeaderMouseDown={handlePartMenuMouseDown}
                            data={partMenuData}
                            onClose={() => setShowPartMenu(false)}
                            onShowParentClick={showParentFromSelection}
                            onZoomToPart={() => sceneRef.current?.zoomToSelectionToggle?.()}
                            onIsolateClick={() => {
                                setIsolateSelectionToggle(true);
                            }}
                            occurrenceToggle={occurrenceToggle}
                            onOccurrenceToggle={handleOccurrenceToggle}
                            isInCart={isInCart}
                            isInRequest={isInRequest}
                            addToCart={canShowAddToCart ? addToCart : null}
                            addToRequestList={canShowAddToRequestList ? addToRequestList : null}
                            showPrice={canShowPrice}
                        />

                    </Box>
                )}

                {/* Multiple parts menu */}
                {showMultiPartMenu && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 80,
                            right: 30,
                            zIndex: 29,
                            pointerEvents: 'auto',
                            transform: `translate(${multiPartMenuDelta.x}px, ${multiPartMenuDelta.y}px)`,
                        }}
                    >
                        <MultiplePartsMenu
                            count={multiPartSelection.length}
                            items={multiPartSelection}
                            machineId={machine_id}
                            onHeaderMouseDown={handleMultiPartMenuMouseDown}
                            onClose={() => setShowMultiPartMenu(false)}
                            onAddToCart={canShowAddToCart ? handleMultipleAddToCart : null}
                            findMatchingProductForPart={findMatchingProductForPart}
                            getSitePrice={getSitePrice}
                            showPrice={canShowPrice}
                        />

                    </Box>
                )}
            </Box>

            {/* Settings Modal */}
            <Dialog
                open={settingsOpen}
                onClose={handleSettingsClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        borderRadius: 2,
                        outline: 'none',
                    },
                }}
            >
                <DialogTitle>Model Settings</DialogTitle>
                <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

                    {/* Default Navigation Mode */}
                    <Box>
                        <Typography fontWeight={600}>Default Navigation Mode</Typography>
                        <RadioGroup
                            value={settings.navMode}
                            onChange={(e) =>
                                setSettings((prev) => ({ ...prev, navMode: e.target.value }))
                            }
                            row
                        >
                            <FormControlLabel value="rotate" control={<Radio />} label="Rotate" />
                            <FormControlLabel value="pan" control={<Radio />} label="Pan" />
                        </RadioGroup>
                    </Box>

                    {/* Transparency toggle */}
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={settings.transparentOnSelect}
                                    onChange={(e) =>
                                        setSettings((prev) => ({
                                            ...prev,
                                            transparentOnSelect: e.target.checked,
                                        }))
                                    }
                                />
                            }
                            label="Make part transparent when selected"
                        />
                    </Box>

                    {/* Explode Mode */}
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={settings.explodeEnabled}
                                    onChange={(e) =>
                                        setSettings((prev) => ({
                                            ...prev,
                                            explodeEnabled: e.target.checked,
                                        }))
                                    }
                                />
                            }
                            label="Enable Explode Mode"
                        />

                        {settings.explodeEnabled && (
                            <Slider
                                value={settings.explodeValue}
                                min={0}
                                max={1}
                                step={0.05}
                                onChange={(_, value) =>
                                    setSettings((prev) => ({ ...prev, explodeValue: value }))
                                }
                                sx={{ width: 200, mt: 1 }}
                            />
                        )}
                    </Box>

                    {/* Auto Zoom */}
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={settings.autoZoom}
                                    onChange={(e) =>
                                        setSettings((prev) => ({
                                            ...prev,
                                            autoZoom: e.target.checked,
                                        }))
                                    }
                                />
                            }
                            label="Auto-zoom to selected part"
                        />
                    </Box>

                    {/* Gizmo Visibility */}
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={settings.showGizmo}
                                    onChange={(e) =>
                                        setSettings((prev) => ({
                                            ...prev,
                                            showGizmo: e.target.checked,
                                        }))
                                    }
                                />
                            }
                            label="Show 3D Navigation Gizmo"
                        />
                    </Box>

                    {/* Light Intensity */}
                    <Box>
                        <Typography fontWeight={600}>{t("LABELS.LIGHT_INTENSITY")}</Typography>
                        <Slider
                            value={settings.light}
                            min={0.2}
                            max={2}
                            step={0.1}
                            onChange={(_, value) =>
                                setSettings((prev) => ({ ...prev, light: value }))
                            }
                            sx={{ width: 200, mt: 1 }}
                        />
                    </Box>

                </DialogContent>

                <DialogActions>
                    <Button onClick={handleSettingsClose}>{t("BUTTON.CLOSE")}</Button>
                    <Button
                        onClick={handleSettingsClose}
                        variant="contained"
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* BOM Modal */}
            <ModalWrapper
                open={bomOpen}
                setOpen={setBomOpen}
                onClose={handleBomClose}
                title="BOM (Bill of Materials)"
                subtitle="Bill of Materials extracted from the 3D model"
                width="70vw"
                content={
                    <Datagrid
                        data={bomData}
                        columns={bomColumns}
                        rowIdField="id"
                        showSearchBar={true}
                        autoHeight={false}
                        gridHeight="60vh"
                        pageSize={5}
                    />
                }
                actions={
                    <>
                        <Button variant="outlined" onClick={handleBomClose}>
                            {t("BUTTON.CLOSE")}

                        </Button>
                    </>
                }
            />

            <ModalWrapper
                open={helpOpen}
                setOpen={setHelpOpen}
                onClose={() => setHelpOpen(false)}
                title="Model Viewer Help"
                subtitle="Quick reference for the icons and controls."
                width="70vw"
                content={helpContent}
                actions={
                    <>
                        <Button
                            variant="contained"
                            startIcon={<SupportAgent />}
                            onClick={handleContactCcrClick}
                        >
                            {t('LABELS.CONTACT_CCR')}
                        </Button>
                        <Button variant="contained" onClick={() => setHelpOpen(false)}>
                            {t("BUTTON.CLOSE")}
                        </Button>
                    </>
                }
            />

            <ContactCCRForm
                open={ccrOpen}
                setOpen={setCcrOpen}
                machine={machine}
                prefill={ccrPrefill}
            />

        </>
    );
};

export default ModelViewer;
